import React, { useEffect, useMemo, useState } from "react";
import { sumCostParts } from "./finance/shared/costParts";
import { useLocation, useNavigate } from "react-router-dom";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import CrudService from "../../../../../../services/cruds-service";
import { useSnackbar } from "context";

import { Autocomplete, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, Grid, IconButton, Radio, RadioGroup, Switch, Tooltip } from "@mui/material";
// Büyük formda her tuşta tüm formun yeniden çizilmesini önleyen tamponlu TextField (yazma gecikmesi)
import TextField from "components/BufferedTextField";

import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
    FaUniversity,
    FaDollarSign,
    FaEuroSign,
    FaLiraSign,
    FaEnvelope,
    FaPhone,
    FaMapMarkerAlt,
    FaBuilding,
} from "react-icons/fa";

import consolidation from "../../Shipment/Consolidation";
import { appConfirm, appAlert } from "components/AppDialog";
import { AddRowButton, RemoveRowButton } from "components/RowActions";
import { bankRowsForInvoice, isForeignCountry } from "./finance/shared/bankAccounts";

const invoiceOptions = [
    { label: "Road Freight Charge" },
    { label: "Fuel Surcharge" },
    { label: "Terminal Handling Charge (THC)" },
    { label: "Customs Brokerage Fee" },
    { label: "Documentation Fee" },
    { label: "Cargo Insurance" },
    { label: "Storage/Warehousing Fee" },
    { label: "Demurrage & Detention" },
    { label: "Packing/Palletizing Fee" },
    { label: "Handling/Forklift Charge" },
    { label: "Peak Season Surcharge" },
    { label: "Currency Adjustment" },
    { label: "Security Fee" },
    { label: "Handling Agent Fee" },
    { label: "Port Congestion Surcharge" },
    { label: "Bill of Lading Fee" },
    { label: "Import/Export Declaration Fee" },
    { label: "Bunker Adjustment Factor" },
    { label: "Container Cleaning Fee" },
    { label: "Container Seal Fee" },
    { label: "Pre-carriage/On-carriage" },
    { label: "Reefer Plug-in Charge" },
    { label: "Weight Discrepancy Fee" },
    { label: "AMS/ENS Fee" },
    { label: "ISPS Fee" },
    { label: "Documentation Amendment Fee" },
];

const CURRENCY_OPTIONS = ["USD", "EUR", "GBP", "TRY"];

const getEntityId = (value) => {
    if (!value) return "";
    if (typeof value === "string" || typeof value === "number")
        return String(value);
    return String(value?._id || value?.id || value?.$oid || "");
};

const unwrapApiEntity = (payload) =>
    payload?.data?.job ||
    payload?.data?.invoice ||
    payload?.data?.data ||
    payload?.invoice ||
    payload?.data ||
    payload ||
    null;

// Editing a saved invoice only has the finance job's shipment summary (no
// vessel, ports, containers or cargo lines) and no master file, so the draft
// PDF's shipment block came out empty. Load the full house record and the
// OMBL/AMBL/RMBL that holds it, the way the shipment pages hand them over
// when an invoice is first created. Any failure just leaves the summary.
const hasValue = (value) =>
    value !== undefined && value !== null && value !== "" && !(Array.isArray(value) && !value.length);

const loadFullShipmentAndMaster = async (shipmentId, sourceType) => {
    if (!/^[0-9a-f]{24}$/i.test(String(shipmentId || ""))) return {};

    const attrs = (res) => res?.data?.attributes || res?.attributes || null;
    const listAttrs = (res) =>
        (Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []).map((entry) => entry?.attributes || entry);
    const holdsShipment = (master) =>
        (Array.isArray(master?.shipments) ? master.shipments : []).some(
            (entry) => getEntityId(entry?.id || entry?._id || entry) === shipmentId,
        );
    const safe = (promise) => promise.catch(() => null);

    const [shipmentRes, masterRes] = await Promise.all(
        sourceType === "ocean"
            ? [safe(CrudService.getOceanShipmentById(shipmentId)), safe(CrudService.getOMBLs({ shipmentId }))]
            : sourceType === "air"
                ? [safe(CrudService.getAirShipmentById(shipmentId)), safe(CrudService.getAMBLs({ shipmentId }))]
                : [safe(CrudService.getShipmentById(shipmentId)), safe(CrudService.getMBLs())],
    );

    return {
        shipment: attrs(shipmentRes),
        master: listAttrs(masterRes).find(holdsShipment) || null,
    };
};

const mergeFullShipment = (summaryEntry, fullShipment) => {
    const summary = summaryEntry?.attributes || summaryEntry || {};
    if (!fullShipment) return summaryEntry;

    return {
        ...summary,
        ...Object.fromEntries(Object.entries(fullShipment).filter(([, value]) => hasValue(value))),
        // Keep the invoice's own display no. and finance-side identity.
        _id: summary._id || summary.id || fullShipment._id,
        shipmentNo: summary.shipmentNo || fullShipment.shipmentNo,
        sourceType: summary.sourceType || fullShipment.sourceType,
    };
};

const getInvoiceShipmentId = (invoice) =>
    getEntityId(
        invoice?.shipmentId ||
        invoice?.shipment_id ||
        invoice?.shipment?._id ||
        invoice?.shipment?.id,
    );

const buildInvoiceShipment = (invoice) => {
    if (!invoice) return null;

    const embeddedShipment = invoice?.shipment?.attributes || invoice?.shipment;
    if (embeddedShipment && typeof embeddedShipment === "object") {
        return embeddedShipment;
    }

    const shipmentId = getInvoiceShipmentId(invoice);
    const invoiceItems = invoice?.invoiceItems || invoice?.invoice_items || [];

    if (!shipmentId && !invoice?.shipmentNo && !invoice?.shipment_no) return null;

    return {
        _id: shipmentId,
        id: shipmentId,
        shipmentNo: invoice?.shipmentNo || invoice?.shipment_no || "",
        sourceType: invoice?.sourceType || invoice?.source_type || "",
        customer:
            invoice?.customerName ||
            invoice?.customer_name ||
            invoice?.customer?.name ||
            invoice?.customer ||
            "",
        email: invoice?.email || invoice?.customerEmail || "",
        sellingRate: invoice?.totalAmount || invoice?.total_amount || 0,
        totalSellingRate: invoice?.totalAmount || invoice?.total_amount || 0,
        sellingRateCurrency:
            invoice?.sellingRateCurrency || invoice?.currency || "",
        currency: invoice?.currency || invoice?.sellingRateCurrency || "",
        totalCost: invoice?.totalCost || invoice?.total_cost || 0,
        actualCostTotal:
            invoice?.actualCostTotal ||
            invoice?.actual_cost_total ||
            invoice?.totalCost ||
            0,
        costCurrency:
            invoice?.costCurrency ||
            invoice?.totalCostCurrency ||
            invoice?.currency ||
            "",
        invoiceItems: Array.isArray(invoiceItems) ? invoiceItems : [],
    };
};

// "YYYY-MM-DD" for a date input, in local time.
const toDateInputValue = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// Date from a date input's "YYYY-MM-DD", at local noon so no time zone moves it to
// another day (the e-invoice IssueDate is written from it).
const fromDateInputValue = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
    return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12) : null;
};

// Payment term (days) agreed with the customer for this mode (ocean / air / road), 0 if none.
const getPaymentTermDays = (customer, mode) =>
    (Array.isArray(customer?.financialInfoByProduct) ? customer.financialInfoByProduct : [])
        .map((info) => Number(info?.financialTerms?.[mode]?.paymentTerms) || Number(info?.paymentTerms) || 0)
        .find((days) => days > 0) || 0;

const FIELD_SX = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "12px",
        backgroundColor: "#fff",
    },
    "& .MuiInputLabel-root": {
        backgroundColor: "#fff",
        px: 0.5,
    },
};

const StatCard = ({ title, value, subtitle, color = "info" }) => (
    <Card
        sx={{
            p: 2,
            borderRadius: "18px",
            height: "100%",
            border: "1px solid rgba(148,163,184,0.22)",
            background: "linear-gradient(145deg, #ffffff, #f8fafc)",
            boxShadow: "0 14px 35px rgba(15,23,42,0.07)",
        }}
    >
        <MDTypography variant="caption" color="text" fontWeight="medium">
            {title}
        </MDTypography>

        <MDTypography variant="h5" fontWeight="bold" color={color} mt={0.5}>
            {value}
        </MDTypography>

        {subtitle && (
            <MDTypography variant="caption" color="text">
                {subtitle}
            </MDTypography>
        )}
    </Card>
);

const SectionCard = ({ title, subtitle, children, rightAction }) => (
    <Card
        sx={{
            p: 2.5,
            mb: 3,
            borderRadius: "22px",
            border: "1px solid rgba(148,163,184,0.22)",
            boxShadow: "0 18px 45px rgba(15,23,42,0.07)",
            background: "linear-gradient(180deg, #ffffff, #f8fafc)",
        }}
    >
        <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
        >
            <MDBox>
                <MDTypography variant="h6" fontWeight="bold">
                    {title}
                </MDTypography>
                {subtitle && (
                    <MDTypography variant="caption" color="text">
                        {subtitle}
                    </MDTypography>
                )}
            </MDBox>

            {rightAction}
        </MDBox>

        <Divider sx={{ mb: 2 }} />
        {children}
    </Card>
);

const BankCard = ({ title, color, rows, swift }) => (
    <Card
        sx={{
            p: 2,
            borderRadius: "18px",
            height: "100%",
            border: "1px solid rgba(148,163,184,0.22)",
            boxShadow: "0 14px 32px rgba(15,23,42,0.07)",
            background: "linear-gradient(145deg, #ffffff, #f8fafc)",
        }}
    >
        <MDTypography variant="h6" fontWeight="bold" sx={{ color, mb: 1.5 }}>
            <FaUniversity style={{ marginRight: 8 }} />
            {title}
        </MDTypography>

        {rows.map((row) => (
            <MDBox key={row.currency} mb={1.2}>
                <MDTypography variant="button" fontWeight="bold" display="block">
                    {row.icon} {row.currency}
                </MDTypography>
                <MDTypography
                    variant="caption"
                    color="text"
                    sx={{ userSelect: "all", wordBreak: "break-word" }}
                >
                    IBAN: {row.iban}
                </MDTypography>
            </MDBox>
        ))}

        <Divider sx={{ my: 1.5 }} />

        <MDTypography variant="caption" color="text">
            <b>SWIFT:</b> {swift || "-"}
        </MDTypography>
    </Card>
);

const ViewShipments = ({ embedded = false, invoiceState = null, onClose }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    const pageState = invoiceState || location.state || {};
    const initialInvoice =
        pageState.invoice ||
        pageState.customerInvoice ||
        pageState.customer_invoice ||
        null;
    const invoiceId = getEntityId(
        pageState.invoiceId || pageState.invoice_id || initialInvoice,
    );
    const isEditMode = Boolean(invoiceId);
    const initialShipments = useMemo(() => {
        const list =
            [
                pageState.shipments,
                pageState.job?.shipments,
                pageState.financeJob?.shipments,
            ].find(Array.isArray) || [];

        if (list.length) return list;

        const invoiceShipment =
            initialInvoice?.shipment || pageState.shipment || pageState.job?.shipment;
        return invoiceShipment ? [invoiceShipment] : [];
    }, [initialInvoice, pageState]);

    const [editingInvoice, setEditingInvoice] = useState(initialInvoice);
    const [shipments, setShipments] = useState(initialShipments);
    const [loadingInvoice, setLoadingInvoice] = useState(Boolean(invoiceId));
    // Master (OMBL/AMBL/RMBL) looked up for an invoice being edited; when the
    // page is opened from a master file, pageState already carries it.
    const [loadedMaster, setLoadedMaster] = useState(null);
    const [invoiceLoadError, setInvoiceLoadError] = useState("");

    const financeJobId = getEntityId(
        pageState.financeJobId ||
        pageState.finance_job_id ||
        editingInvoice?.financeJobId ||
        editingInvoice?.finance_job_id ||
        pageState.job?.financeJobId ||
        pageState.job?.finance_job_id ||
        pageState.job,
    );
    // pageState.sourceType is only ever set for the ShipmentTable-driven
    // flow; FinanceJobDetail's "Create Invoice" button sets sourceType on
    // the shipment entry itself instead, which made this always fall
    // through to "" ("N/A" in the UI) for that entry point.
    const sourceType =
        editingInvoice?.sourceType ||
        editingInvoice?.source_type ||
        pageState.sourceType ||
        (shipments?.[0]?.attributes || shipments?.[0])?.sourceType ||
        (shipments?.[0]?.attributes || shipments?.[0])?.source_type ||
        "";

    const [open, setOpen] = useState(false);
    const [selectedShipmentNo, setSelectedShipmentNo] = useState("");
    const [selectedShipment, setSelectedShipment] = useState(null);
    const [customerDetails, setCustomerDetails] = useState(null);
    const [serviceProviderDetails, setServiceProviderDetails] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [draftGenerated, setDraftGenerated] = useState(false);
    const [saving, setSaving] = useState(false);
    const [autoOpened, setAutoOpened] = useState(false);
    // GİB's "KDV istisna sebep kodu" (muhasebe kodu) the invoice's 0% VAT
    // line should carry -- fetched from our own backend (sourced from GİB's
    // own published UBL-TR Kod Listeleri), not hardcoded, since different
    // shipments have different legal exemption reasons (export vs.
    // international transport vs. ...), not just the "311" default.
    const [exemptionCodes, setExemptionCodes] = useState({ fullExemption: [], partialExemption: [] });
    const [selectedExemptionCode, setSelectedExemptionCode] = useState(null);
    // Invoice (issue) date, entered by hand: invoices are often cut after the vessel has
    // sailed. Stored as "YYYY-MM-DD"; used on the PDF, as invoiceDate (also the e-invoice
    // IssueDate) and as the start of the due date. Locked once the e-invoice is sent.
    const [invoiceDate, setInvoiceDate] = useState(() => toDateInputValue(new Date()));
    const invoiceDateLocked = editingInvoice?.edm?.status === "Sent";
    // Per the manager's confirmed rule (chat screenshot, Mehmet İkmaz):
    // only digital freight + international transport invoices are
    // "İstisna" (0% KDV, e.g. 311 or the TGB code 223 for tech invoices) --
    // everything else is a regular "Satış" invoice at the standard 20% KDV
    // rate. Defaults to exempt since that's the company's majority invoice
    // type (freight forwarding), matching the pre-existing behavior before
    // this toggle existed.
    const STANDARD_VAT_RATE = 20;
    const [isExemptInvoice, setIsExemptInvoice] = useState(true);
    // Exempt invoices only: split the selling into a "normal" line and a
    // "technology" line (the technology share of the PROFIT, own exemption code).
    // The draft PDF stays one "All In" line; only the EDM document gets the 2 lines.
    const [profitSplitEnabled, setProfitSplitEnabled] = useState(false);
    const [technologyPercent, setTechnologyPercent] = useState(70);
    const [technologyExemptionCode, setTechnologyExemptionCode] = useState("223");
    const [costInSellingCurrency, setCostInSellingCurrency] = useState(0);

    const linehaulServiceProviderMap = pageState.linehaulServiceProviderMap || {};
    const linehaulServiceProvider =
        linehaulServiceProviderMap[selectedShipmentNo] || null;

    useEffect(() => {
        if (!invoiceId) {
            setLoadingInvoice(false);
            return undefined;
        }

        let active = true;

        const loadInvoice = async () => {
            try {
                setLoadingInvoice(true);
                setInvoiceLoadError("");

                const apiUrl = String(process.env.REACT_APP_API_URL || "").replace(
                    /\/$/,
                    "",
                );

                if (!apiUrl) {
                    throw new Error("REACT_APP_API_URL tanımlı değil.");
                }

                const headers = {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                };
                const response = await fetch(`${apiUrl}/invoice/${invoiceId}`, {
                    headers,
                });
                const contentType = response.headers.get("content-type") || "";

                if (!response.ok || !contentType.includes("application/json")) {
                    throw new Error(
                        `Fatura yüklenemedi (HTTP ${response.status}, ${response.url}).`,
                    );
                }

                const invoice = unwrapApiEntity(await response.json());
                if (!invoice) throw new Error("Fatura yanıtı boş geldi.");

                let resolvedShipments = [];
                const embeddedShipment = buildInvoiceShipment(invoice);
                const shipmentId = getInvoiceShipmentId(invoice);
                const resolvedFinanceJobId = getEntityId(
                    invoice?.financeJobId ||
                    invoice?.finance_job_id ||
                    pageState.financeJobId ||
                    pageState.finance_job_id,
                );

                if (resolvedFinanceJobId) {
                    const jobResponse = await fetch(
                        `${apiUrl}/finance/jobs/${resolvedFinanceJobId}`,
                        { headers },
                    ).catch(() => null);

                    if (jobResponse?.ok) {
                        const job = unwrapApiEntity(await jobResponse.json());
                        const candidates = [
                            ...(Array.isArray(job?.shipments) ? job.shipments : []),
                            ...(job?.shipment ? [job.shipment] : []),
                            ...(job ? [job] : []),
                        ];
                        const matchingShipment = candidates.find((candidate) => {
                            const data = candidate?.attributes || candidate || {};
                            return (
                                shipmentId &&
                                getEntityId(data?._id || data?.id || candidate) === shipmentId
                            );
                        });

                        if (matchingShipment) resolvedShipments = [matchingShipment];
                    }
                }

                if (!resolvedShipments.length && embeddedShipment) {
                    resolvedShipments = [embeddedShipment];
                }

                if (!resolvedShipments.length) {
                    const matchingStateShipment = initialShipments.find((candidate) => {
                        const data = candidate?.attributes || candidate || {};
                        return (
                            !shipmentId ||
                            getEntityId(data?._id || data?.id || candidate) === shipmentId
                        );
                    });
                    if (matchingStateShipment)
                        resolvedShipments = [matchingStateShipment];
                }

                const invoiceSourceType = String(
                    invoice?.sourceType ||
                    invoice?.source_type ||
                    (resolvedShipments[0]?.attributes || resolvedShipments[0])?.sourceType ||
                    (/^OHFL/i.test(invoice?.shipmentNo || "") ? "ocean" : /^AHFL/i.test(invoice?.shipmentNo || "") ? "air" : "road"),
                ).toLowerCase();
                const { shipment: fullShipment, master } = await loadFullShipmentAndMaster(
                    shipmentId,
                    invoiceSourceType,
                );

                if (fullShipment) {
                    resolvedShipments = [
                        mergeFullShipment(resolvedShipments[0], { ...fullShipment, sourceType: invoiceSourceType }),
                    ];
                }

                if (invoice.dblNumber && resolvedShipments[0]) {
                    resolvedShipments = [{ ...resolvedShipments[0], dblNumber: invoice.dblNumber }];
                }

                if (active) {
                    setLoadedMaster(master);
                    setEditingInvoice(invoice);
                    setShipments(resolvedShipments);
                    if (invoice.exemptionCode) setSelectedExemptionCode(invoice.exemptionCode);
                    if (invoice.invoiceDate) setInvoiceDate(toDateInputValue(invoice.invoiceDate));
                    // Same rule ublBuilder.js uses for InvoiceTypeCode: any
                    // 0%-VAT line means this was an exempt ("İstisna")
                    // invoice; a non-empty items list with no 0% line means
                    // it was a regular "Satış" invoice.
                    const items = invoice.invoiceItems || [];
                    if (items.length) {
                        setIsExemptInvoice(items.some((item) => Number(item.vatRate) === 0));
                    }
                    if (invoice.profitSplit?.enabled) {
                        setProfitSplitEnabled(true);
                        setTechnologyPercent(invoice.profitSplit.technologyPercent ?? 70);
                        setTechnologyExemptionCode(invoice.profitSplit.technologyExemptionCode || "223");
                    }
                }
            } catch (error) {
                console.error("Customer invoice load error:", error);
                if (active) setInvoiceLoadError(error.message);
            } finally {
                if (active) setLoadingInvoice(false);
            }
        };

        loadInvoice();

        return () => {
            active = false;
        };
    }, [invoiceId]);

    useEffect(() => {
        let active = true;

        const loadExemptionCodes = async () => {
            try {
                const apiUrl = String(process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
                const response = await fetch(`${apiUrl}/edm/exemption-codes`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                });
                const result = await response.json();
                if (!active || !response.ok || !result.success) return;

                setExemptionCodes(result.data);
                setSelectedExemptionCode((current) => current || "311");
            } catch (error) {
                console.error("Exemption code list load error:", error);
            }
        };

        loadExemptionCodes();

        return () => {
            active = false;
        };
    }, []);

    const normalizeText = (value = "") =>
        String(value).toLowerCase().trim().replace(/\s+/g, " ");

    const safeNumber = (value) => {
        const n = Number(value);
        return Number.isFinite(n) ? n : 0;
    };

    const firstPositiveNumber = (...values) => {
        for (const value of values) {
            const number = safeNumber(value);
            if (number > 0) return number;
        }

        return 0;
    };

    const getChargeTotal = (charge) =>
        firstPositiveNumber(
            charge?.total,
            charge?.totalCost,
            charge?.cost,
            charge?.buyingRate,
            charge,
        );

    const getIncomingTotalCost = (data = {}) => {
        const summary = data.financialSummary || {};
        const freightCharges = data.freightCharges || {};

        const explicitCost = firstPositiveNumber(
            data.totalCost,
            data.actualCostTotal,
            data.expectedCostTotal,
            data.operationalCost,
            data.cost,
            summary.totalCost,
            summary.actualCostTotal,
            summary.expectedCostTotal,
        );

        if (explicitCost > 0) return explicitCost;

        const roadCost =
            safeNumber(summary.shipmentCostTotal) +
            safeNumber(summary.convertedLinehaulCost) +
            safeNumber(summary.convertedHandlingCost);

        const oceanOrAirCost =
            safeNumber(summary.pickupTotal) +
            safeNumber(summary.othcTotal) +
            safeNumber(summary.oceanFreightTotal || summary.airFreightTotal) +
            safeNumber(summary.dthcTotal) +
            safeNumber(summary.handlingTotal) +
            safeNumber(summary.deliveryTotal);

        const freightChargesCost =
            firstPositiveNumber(
                freightCharges.pickupTotal,
                getChargeTotal(freightCharges.pickup),
            ) +
            getChargeTotal(freightCharges.othc) +
            getChargeTotal(freightCharges.oceanFreight) +
            getChargeTotal(freightCharges.airFreight) +
            getChargeTotal(freightCharges.dthc) +
            getChargeTotal(freightCharges.handling) +
            firstPositiveNumber(
                freightCharges.deliveryTotal,
                getChargeTotal(freightCharges.delivery),
            );

        return firstPositiveNumber(
            roadCost,
            oceanOrAirCost,
            freightChargesCost,
            data.buyingRate,
            summary.buyingRate,
            freightCharges.buyingRate,
        );
    };

    // Mirrors getIncomingTotalCost's fallback cascade for the sales/selling
    // side. A shipment picked from a shipment table carries a plain
    // `sellingRate`, but one passed in from FinanceJobDetail's "Create
    // Invoice" button carries the same amount under the finance-job field
    // names (invoiceSalesAmount/totalAmount/...) instead -- reading only
    // `sellingRate` made "Total Selling Rate" show 0.00 for every invoice
    // started from a finance job page.
    // An ocean shipment's full selling amount is `finalSellingRate` (base rate plus its
    // additional fees); `sellingRate` is only the base, 0 when the whole price is in fees.
    const getIncomingSellingAmount = (data = {}) => {
        const summary = data.financialSummary || {};

        return firstPositiveNumber(
            data.finalSellingRate,
            data.sellingRate,
            data.totalSellingRate,
            data.invoiceSalesAmount,
            data.salesAmount,
            data.totalAmount,
            data.customerInvoiceAmount,
            data.actualRevenueTotal,
            summary.totalSellingRate,
        );
    };

    const getItemLabel = (item, fallback = "Freight Charge") => {
        const rawLabel =
            item?.commodity?.label ||
            item?.commodity ||
            item?.label ||
            item?.name ||
            item?.title ||
            item?.chargeName ||
            item?.costName ||
            item?.type ||
            item?.key ||
            fallback;

        return typeof rawLabel === "object" ? fallback : String(rawLabel);
    };

    const normalizeInvoiceItem = (
        item = {},
        fallbackLabel = "Freight Charge",
    ) => {
        const quantity =
            safeNumber(item.unitQuantity ?? item.quantity ?? item.qty ?? 1) || 1;
        const totalCost = safeNumber(
            item.totalCost ?? item.cost ?? item.buyingRate ?? item.unitCost,
        );
        const sellingRate = safeNumber(
            item.sellingRate ??
            item.unitPrice ??
            item.salesPrice ??
            item.price ??
            item.amount,
        );
        const costCurrency =
            item.costCurrency ||
            item.totalCostCurrency ||
            item.buyingRateCurrency ||
            "";
        const sellingRateCurrency =
            item.sellingRateCurrency || item.salesCurrency || item.currency || "";

        return {
            ...item,
            commodity: getItemLabel(item, fallbackLabel),
            description: item.description || item.details || "",
            totalCost,
            costCurrency,
            unitPrice: sellingRate,
            sellingRateCurrency,
            unitQuantity: quantity,
            amount: sellingRate * quantity,
        };
    };

    const pricingObjectToItems = (pricing) => {
        if (!pricing) return [];

        if (Array.isArray(pricing)) {
            return pricing.map((item, index) =>
                normalizeInvoiceItem(item, `Freight Charge ${index + 1}`),
            );
        }

        if (typeof pricing !== "object") return [];

        return Object.entries(pricing).map(([key, value]) =>
            normalizeInvoiceItem(
                typeof value === "object" && value !== null
                    ? { ...value, key }
                    : { key, sellingRate: value },
                key,
            ),
        );
    };

    const buildInvoiceLineItems = (data = {}) => {
        const defaultCostCurrency =
            data.costCurrency ||
            data.totalCostCurrency ||
            data.buyingRateCurrency ||
            data.financialSummary?.costCurrency ||
            data.financialSummary?.totalCurrency ||
            "EUR";
        const defaultSellingCurrency =
            data.sellingRateCurrency ||
            data.salesCurrency ||
            data.currency ||
            data.financialSummary?.totalCurrency ||
            "EUR";
        const itemSources = [
            { value: data.invoiceItems, requiresPrice: false },
            { value: data.shipmentPricing, requiresPrice: false },
            { value: data.pricingDetails, requiresPrice: false },
            { value: data.sellingRateItems, requiresPrice: false },
            { value: data.costItems, requiresPrice: false },
            { value: data.extraCosts, requiresPrice: false },
            // cargoDetails çoğu shipment kaydında yalnızca yük ölçülerini içerir.
            // Fiyat alanı yoksa invoice satırı olarak kullanılmamalıdır.
            { value: data.cargoDetails, requiresPrice: true },
        ];

        for (const source of itemSources) {
            const items = pricingObjectToItems(source.value).filter((item) => {
                const hasPrice = item.totalCost !== 0 || item.unitPrice !== 0;
                return source.requiresPrice
                    ? hasPrice
                    : item.commodity || item.description || hasPrice;
            });

            if (items.length > 0) {
                const hasItemCost = items.some((item) => item.totalCost > 0);
                const incomingTotalCost = getIncomingTotalCost(data);

                if (!hasItemCost && incomingTotalCost > 0) {
                    return items.map((item, index) => ({
                        ...item,
                        totalCost: index === 0 ? incomingTotalCost : item.totalCost,
                        costCurrency: item.costCurrency || defaultCostCurrency,
                        sellingRateCurrency:
                            item.sellingRateCurrency || defaultSellingCurrency,
                    }));
                }

                return items.map((item) => ({
                    ...item,
                    costCurrency: item.costCurrency || defaultCostCurrency,
                    sellingRateCurrency:
                        item.sellingRateCurrency || defaultSellingCurrency,
                }));
            }
        }

        return [
            normalizeInvoiceItem(
                {
                    commodity: "Freight Charge",
                    description: "Shipment freight service",
                    totalCost: getIncomingTotalCost(data),
                    costCurrency: defaultCostCurrency,
                    sellingRate: getIncomingSellingAmount(data),
                    sellingRateCurrency: defaultSellingCurrency,
                    unitQuantity: 1,
                },
                "Freight Charge",
            ),
        ];
    };

    const getShipmentPrefix = (shipment) => {
        const rawNo = String(
            shipment?.shipmentNo || shipment?.attributes?.shipmentNo || "",
        )
            .trim()
            .toUpperCase();

        if (rawNo.startsWith("AHFL")) return "AHFL";

        const shipmentSourceType = String(
            shipment?.sourceType ||
            shipment?.attributes?.sourceType ||
            sourceType ||
            "",
        ).toLowerCase();

        if (shipmentSourceType === "air") return "AHFL";

        const mode = [
            shipment?.airTransportType,
            shipment?.attributes?.airTransportType,
            shipment?.oceanTransportType,
            shipment?.attributes?.oceanTransportType,
            shipment?.roadTransportType,
            shipment?.attributes?.roadTransportType,
            shipment?.transportMode,
            shipment?.attributes?.transportMode,
            shipment?.serviceType,
            shipment?.attributes?.serviceType,
        ]
            .filter(Boolean)
            .join(" ")
            .toUpperCase();

        if (
            mode.includes("AIR") ||
            mode.includes("FLIGHT") ||
            mode.includes("AIRPORT")
        ) {
            return "AHFL";
        }

        if (rawNo.startsWith("OHFL")) return "OHFL";
        if (rawNo.startsWith("RHFL")) return "RHFL";

        if (shipmentSourceType === "ocean") return "OHFL";
        if (shipmentSourceType === "road") return "RHFL";

        if (mode.includes("FCL") || mode.includes("LCL")) return "OHFL";
        if (mode.includes("FTL") || mode.includes("LTL")) return "RHFL";

        return "RHFL";
    };

    const getShipmentNumericNo = (shipment, index = 0) => {
        const rawNo = String(
            shipment?.shipmentNo || shipment?.attributes?.shipmentNo || index + 1,
        )
            .trim()
            .toUpperCase();

        if (rawNo.startsWith("OHFL")) return rawNo.replace("OHFL", "");
        if (rawNo.startsWith("RHFL")) return rawNo.replace("RHFL", "");
        if (rawNo.startsWith("AHFL")) return rawNo.replace("AHFL", "");

        return rawNo;
    };

    const getShipmentDisplayNo = (shipment, index = 0) =>
        `${getShipmentPrefix(shipment)}${getShipmentNumericNo(shipment, index)}`;

    // Invoices cut from a DBL form show the DBL number as the shipment. The stored
    // shipmentNo stays the house file number: finance pages find the shipment by it.
    const getShipmentDblNumber = (shipment) =>
        String(shipment?.dblNumber || shipment?.attributes?.dblNumber || "").trim();

    const getShipmentLabel = (shipment, index = 0) =>
        getShipmentDblNumber(shipment) || getShipmentDisplayNo(shipment, index);

    const getInvoiceSourceType = (shipment) => {
        const prefix = getShipmentPrefix(shipment);

        if (prefix === "OHFL") return "ocean";
        if (prefix === "AHFL") return "air";
        return "road";
    };

    const totalSellingAmount = useMemo(() => {
        return shipments.reduce((sum, shipment) => {
            const data = shipment?.attributes || shipment || {};
            return sum + getIncomingSellingAmount(data);
        }, 0);
    }, [shipments]);

    const defaultCurrency =
        shipments?.[0]?.sellingRateCurrency ||
        shipments?.[0]?.attributes?.sellingRateCurrency ||
        "";

    const totalInvoiceAmount = useMemo(() => {
        if (!selectedShipment?.cargoDetails) return 0;

        return selectedShipment.cargoDetails.reduce(
            (sum, item) =>
                sum + safeNumber(item.unitPrice) * safeNumber(item.unitQuantity),
            0,
        );
    }, [selectedShipment]);

    // VAT-exclusive subtotal unless this is a "Satış" (non-exempt) invoice,
    // in which case the standard 20% KDV is added -- shown in the preview so
    // what the operator sees before confirming matches what the backend's
    // own invoiceItems-based total ends up saving.
    const totalInvoiceAmountWithVat = useMemo(() => {
        return isExemptInvoice
            ? totalInvoiceAmount
            : totalInvoiceAmount * (1 + STANDARD_VAT_RATE / 100);
    }, [totalInvoiceAmount, isExemptInvoice]);

    const totalInvoiceCost = useMemo(() => {
        if (!selectedShipment?.cargoDetails) return 0;

        return selectedShipment.cargoDetails.reduce(
            (sum, item) => sum + safeNumber(item.totalCost),
            0,
        );
    }, [selectedShipment]);

    // Profit = selling - cost, in the invoice's selling currency (line costs may
    // be entered in another currency, so they are converted first).
    useEffect(() => {
        let active = true;
        const lines = selectedShipment?.cargoDetails || [];
        const fallbackCurrency = selectedShipment?.costCurrency || "";

        sumCostParts(
            lines.map((d) => ({
                amount: safeNumber(d.totalCost),
                currency: d.costCurrency || fallbackCurrency,
            })),
            selectedShipment?.sellingRateCurrency || "",
            fetchExchangeRateToTRY,
        ).then((value) => {
            if (active) setCostInSellingCurrency(value);
        });

        return () => {
            active = false;
        };
    }, [
        selectedShipment?.cargoDetails,
        selectedShipment?.sellingRateCurrency,
        selectedShipment?.costCurrency,
    ]);

    const profitSplit = useMemo(() => {
        const round2 = (value) => Math.round(value * 100) / 100;
        const selling = round2(totalInvoiceAmount);
        const cost = round2(costInSellingCurrency);
        const profit = Math.max(round2(selling - cost), 0);
        const percent = Math.min(Math.max(Number(technologyPercent) || 0, 0), 100);
        // 100% means the WHOLE invoice is the technology (selected) exemption:
        // no normal line at all, cost included. Below 100% only that share of
        // the profit is technology and cost stays on the normal line.
        const wholeInvoice = percent >= 100;
        const technologyAmount = wholeInvoice ? selling : round2((profit * percent) / 100);
        const normalAmount = round2(selling - technologyAmount);

        return {
            selling,
            cost,
            profit,
            percent,
            wholeInvoice,
            technologyAmount,
            normalAmount,
            active: isExemptInvoice && profitSplitEnabled && technologyAmount > 0,
        };
    }, [totalInvoiceAmount, costInSellingCurrency, technologyPercent, isExemptInvoice, profitSplitEnabled]);

    const loadImageAsBase64 = (url) =>
        new Promise((resolve, reject) => {
            fetch(url)
                .then((res) => res.blob())
                .then((blob) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                })
                .catch(reject);
        });

    // The Invoice schema defaults exchangeRate to 1 and nothing here ever
    // overrode it, so every non-TRY invoice sent to EDM declared a 1:1
    // EUR/USD/GBP -> TRY rate. EDM/GİB compute the invoice's TL-equivalent
    // totals directly from that rate (<cbc:CalculationRate> in the UBL),
    // so a real €2,580 invoice would show "2.580,00 TL" -- the exact same
    // number just relabeled, instead of a real converted TRY amount.
    // With a date: the TCMB rate for an invoice on that date (forex buying rate of the previous
    // business day, from our backend). Without one, or when TCMB cannot be reached: today's
    // market rate, as before.
    const fetchExchangeRateToTRY = async (currency, invoiceDateValue) => {
        const code = String(currency || "").toUpperCase();
        if (!code || code === "TRY") return 1;

        if (invoiceDateValue) {
            try {
                const baseUrl = String(process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
                const response = await fetch(
                    `${baseUrl}/invoice/exchange-rate?currency=${encodeURIComponent(code)}&date=${encodeURIComponent(invoiceDateValue)}`,
                    { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
                );
                const result = await response.json().catch(() => ({}));
                const rate = Number(result?.data?.rate);
                if (response.ok && Number.isFinite(rate) && rate > 0) return rate;
            } catch (error) {
                console.error("TCMB exchange rate fetch error:", error);
            }
        }

        try {
            const response = await fetch(
                `https://api.exchangerate-api.com/v4/latest/${code}`,
            );
            if (!response.ok) throw new Error("Exchange rate request failed");
            const data = await response.json();
            const rate = Number(data?.rates?.TRY);
            return Number.isFinite(rate) && rate > 0 ? rate : 1;
        } catch (error) {
            console.error("Exchange rate fetch error:", error);
            return 1;
        }
    };

    const handleOpen = () => {
        const editShipmentId = getEntityId(
            editingInvoice?.shipmentId || editingInvoice?.shipment_id,
        );
        const editShipmentIndex = shipments.findIndex((shipment) => {
            const data = shipment?.attributes || shipment || {};
            return getEntityId(data?._id || data?.id || shipment) === editShipmentId;
        });

        setSelectedShipmentNo(
            isEditMode && editShipmentIndex >= 0
                ? getShipmentDisplayNo(shipments[editShipmentIndex], editShipmentIndex)
                : "",
        );
        setSelectedShipment(null);
        setCustomerDetails(null);
        setServiceProviderDetails(null);
        setShowDetails(false);
        setDraftGenerated(false);
        // A new invoice starts from today; an invoice being edited keeps its saved date.
        if (!isEditMode) setInvoiceDate(toDateInputValue(new Date()));
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedShipmentNo("");
        setSelectedShipment(null);
        setCustomerDetails(null);
        setServiceProviderDetails(null);
        setShowDetails(false);
        setDraftGenerated(false);
    };

    useEffect(() => {
        if (
            pageState.openInvoiceModal &&
            !loadingInvoice &&
            !invoiceLoadError &&
            shipments.length &&
            !autoOpened
        ) {
            setAutoOpened(true);
            handleOpen();
        }
    }, [
        autoOpened,
        invoiceLoadError,
        loadingInvoice,
        pageState.openInvoiceModal,
        shipments,
    ]);

    const fetchCustomerDetails = async (customerName) => {
        try {
            if (!customerName) return null;

            const response = await CrudService.getCustomers();

            if (!Array.isArray(response.data)) {
                throw new Error("Customer data array değil!");
            }

            const normalizedCustomerName = normalizeText(customerName);

            const customer = response.data.find((c) => {
                const dbName = normalizeText(c?.attributes?.name);
                return dbName === normalizedCustomerName;
            });

            return customer ? customer.attributes : null;
        } catch (error) {
            console.error("API Hatası:", error);
            return null;
        }
    };

    const fetchServiceProviderDetails = async (providerName) => {
        try {
            if (!providerName) return null;

            const response = await CrudService.getServiceProviders();
            const providers = response.data || [];

            const match = providers.find(
                (p) =>
                    p?.attributes?.name?.toLowerCase().trim() ===
                    providerName?.toLowerCase().trim(),
            );

            return match?.attributes || null;
        } catch (error) {
            console.error("Service Provider fetch error:", error);
            return null;
        }
    };

    const handlePreview = async () => {
        const shipment = shipments.find(
            (item, index) => getShipmentDisplayNo(item, index) === selectedShipmentNo,
        );

        if (!shipment) {
            appAlert("Lütfen bir shipment seçin!");
            return;
        }

        const data = shipment?.attributes || shipment || {};

        const customerData = await fetchCustomerDetails(data.customer);

        const serviceProviderName =
            consolidation?.attributes?.linehaulServiceProvider ||
            linehaulServiceProviderMap[selectedShipmentNo] ||
            null;

        const providerData = await fetchServiceProviderDetails(serviceProviderName);

        const existingItems = Array.isArray(editingInvoice?.invoiceItems)
            ? editingInvoice.invoiceItems
            : Array.isArray(editingInvoice?.invoice_items)
                ? editingInvoice.invoice_items
                : [];

        // Cost of the invoice = house (H) file cost + master (M) file cost.
        // The consolidation pages hand both sides over as separate parts (the
        // M cost already split across its H files); they are converted to one
        // currency and summed here. A saved invoice being edited keeps its own
        // stored line costs untouched.
        let lineSource = data;
        let costBreakdown = null;
        const houseParts = Array.isArray(data.houseCostParts) ? data.houseCostParts : [];
        const masterParts = Array.isArray(data.masterCostParts) ? data.masterCostParts : [];

        if (!isEditMode && (houseParts.length || masterParts.length)) {
            const costCurrency =
                data.costCurrency ||
                data.totalCostCurrency ||
                data.buyingRateCurrency ||
                data.financialSummary?.costCurrency ||
                houseParts[0]?.currency ||
                masterParts[0]?.currency ||
                "EUR";
            const houseCost = await sumCostParts(houseParts, costCurrency, fetchExchangeRateToTRY);
            const masterCost = await sumCostParts(masterParts, costCurrency, fetchExchangeRateToTRY);

            if (houseCost > 0) {
                lineSource = {
                    ...data,
                    costCurrency,
                    totalCost: houseCost,
                    actualCostTotal: houseCost,
                    expectedCostTotal: houseCost,
                    operationalCost: houseCost,
                };
            }

            costBreakdown = {
                currency: costCurrency,
                masterCost,
                masterNo: data.masterNo || "",
                masterShipmentCount: data.masterShipmentCount || 1,
                masterParts,
            };
        }

        const builtLines =
            isEditMode && existingItems.length
                ? existingItems.map((item, index) =>
                    normalizeInvoiceItem(item, `Freight Charge ${index + 1}`),
                )
                : buildInvoiceLineItems(lineSource);

        // The M-side share rides on the first line, like the single total cost
        // always did -- extra zero-price lines would reach the customer's invoice.
        if (costBreakdown?.masterCost > 0 && builtLines.length) {
            builtLines[0] = {
                ...builtLines[0],
                totalCost:
                    Math.round(
                        (safeNumber(builtLines[0].totalCost) + costBreakdown.masterCost) * 100,
                    ) / 100,
                costCurrency: builtLines[0].costCurrency || costBreakdown.currency,
            };
        }

        setSelectedShipment({
            ...data,
            costBreakdown,
            _id: data._id || data.id || shipment?._id || shipment?.id,
            sellingRateCurrency:
                data.sellingRateCurrency ||
                data.currency ||
                data.totalCurrency ||
                data.financialSummary?.totalCurrency ||
                "",
            costCurrency:
                data.costCurrency ||
                data.totalCostCurrency ||
                data.buyingRateCurrency ||
                data.financialSummary?.costCurrency ||
                data.financialSummary?.totalCurrency ||
                "",
            cargoDetails: builtLines,
            // Raw cargo lines (weights, packages, containers) for the invoice's shipment block
            shipmentCargo: Array.isArray(data.cargoDetails) ? data.cargoDetails : [],
        });

        setCustomerDetails({
            ...(customerData || {}),
            email: editingInvoice?.email || customerData?.email || data.email || "",
            phone: customerData?.phone || data.phone || "",
            taxID: customerData?.taxID || data.taxID || "",
            billingAddress: customerData?.billingAddress || data.billingAddress || "",
        });

        setServiceProviderDetails(providerData);
        setShowDetails(true);
    };

    const clickHandler = (shipment) => {
        const data = shipment?.attributes || shipment || {};
        const shipmentId = getEntityId(
            data?._id ||
            data?.id ||
            data?.shipmentId ||
            data?.shipment_id ||
            shipment?._id ||
            shipment?.id,
        );

        if (!shipmentId) return;

        const shipmentType = getInvoiceSourceType(data);

        if (shipmentType === "ocean") {
            navigate(
                `/shipping-order/create-ocean-shipment/details?id=${shipmentId}`,
            );
            return;
        }

        if (shipmentType === "air") {
            navigate(
                `/shipping-order/create-air-shipment/details?id=${shipmentId}`,
            );
            return;
        }

        navigate(`/shipping-order/create-shipment/details?id=${shipmentId}`);
    };

    const updateCargoDetail = (index, field, value) => {
        const updatedDetails = [...(selectedShipment?.cargoDetails || [])];
        updatedDetails[index] = {
            ...updatedDetails[index],
            [field]: value,
        };

        setSelectedShipment({
            ...selectedShipment,
            cargoDetails: updatedDetails,
        });
    };

    const addCargoDetail = () => {
        setSelectedShipment({
            ...selectedShipment,
            cargoDetails: [
                ...(selectedShipment?.cargoDetails || []),
                {
                    commodity: "",
                    description: "",
                    totalCost: 0,
                    unitPrice: 0,
                    unitQuantity: 1,
                    amount: 0,
                },
            ],
        });
    };

    const removeCargoDetail = (index) => {
        const updatedDetails = [...(selectedShipment?.cargoDetails || [])];
        updatedDetails.splice(index, 1);

        setSelectedShipment({
            ...selectedShipment,
            cargoDetails: updatedDetails,
        });
    };

    const handleConfirm = async () => {
        try {
            setSaving(true);
            const apiUrl = String(process.env.REACT_APP_API_URL || "").replace(
                /\/$/,
                "",
            );

            if (!apiUrl) {
                throw new Error(
                    "REACT_APP_API_URL tanımlı değil. Frontend ortam değişkenlerini kontrol edin.",
                );
            }

            const readApiResponse = async (response, operation) => {
                const contentType = response.headers.get("content-type") || "";
                const rawBody = await response.text();
                let result = {};

                if (rawBody && contentType.includes("application/json")) {
                    try {
                        result = JSON.parse(rawBody);
                    } catch (_error) {
                        throw new Error(
                            `${operation}: Sunucu geçersiz JSON döndürdü (HTTP ${response.status}).`,
                        );
                    }
                } else if (rawBody) {
                    const isHtml = /<!doctype|<html/i.test(rawBody);
                    throw new Error(
                        isHtml
                            ? `${operation}: API yerine HTML döndü (HTTP ${response.status}, ${response.url}). Route/proxy ayarını kontrol edin.`
                            : `${operation}: Beklenmeyen sunucu yanıtı (HTTP ${response.status}): ${rawBody.slice(0, 180)}`,
                    );
                }

                if (!response.ok) {
                    throw new Error(
                        result.error ||
                        result.message ||
                        result.code ||
                        `${operation} başarısız oldu (HTTP ${response.status}).`,
                    );
                }

                return result;
            };

            if (!isEditMode) {
                const shipmentIdForCheck = getEntityId(
                    selectedShipment?._id ||
                    selectedShipment?.id ||
                    selectedShipment?.shipmentId ||
                    selectedShipment?.shipment_id,
                );

                if (shipmentIdForCheck) {
                    try {
                        const existingRes = await fetch(
                            `${apiUrl}/invoice?shipmentId=${encodeURIComponent(shipmentIdForCheck)}`,
                            {
                                headers: {
                                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                                },
                            },
                        );

                        if (existingRes.ok) {
                            const existingInvoices = await existingRes.json();

                            if (Array.isArray(existingInvoices) && existingInvoices.length > 0) {
                                const existingNo =
                                    existingInvoices[0]?.invoiceNo ||
                                    existingInvoices[0]?._id ||
                                    "?";
                                const proceed = await appConfirm(
                                    `This shipment already has an invoice (${existingNo}). Create another invoice for it anyway?`,
                                );

                                if (!proceed) {
                                    setSaving(false);
                                    return;
                                }
                            }
                        }
                    } catch (checkError) {
                        // Duplicate kontrolü başarısız olsa bile faturalama akışını engelleme;
                        // sadece kontrol atlanmış olur.
                        console.error("Duplicate invoice check failed:", checkError);
                    }
                }
            }

            const doc = await createProformaPDF(selectedShipment, customerDetails);
            const arrayBuffer = doc.output("arraybuffer");
            const pdfFile = new File([arrayBuffer], "invoice.pdf", {
                type: "application/pdf",
            });

            const formData = new FormData();
            const recipientEmail = String(customerDetails?.email || "").trim();

            if (!recipientEmail) {
                throw new Error(
                    "Müşteri e-posta adresi bulunamadı. Customer kaydına e-posta ekleyin.",
                );
            }

            formData.append("file", pdfFile);
            formData.append("customerName", selectedShipment.customer || "Customer");
            formData.append("email", recipientEmail);

            const emailShipmentId = getEntityId(
                selectedShipment?._id ||
                selectedShipment?.id ||
                selectedShipment?.shipmentId ||
                selectedShipment?.shipment_id,
            );

            if (!emailShipmentId) {
                throw new Error(
                    "Shipment ID bulunamadı. Fatura e-postası gönderilemedi.",
                );
            }

            formData.append("shipmentId", emailShipmentId);
            formData.append(
                "shipmentType",
                getInvoiceSourceType(selectedShipment),
            );
            if (financeJobId) formData.append("financeJobId", financeJobId);
            if (invoiceId) formData.append("invoiceId", invoiceId);

            const uploadResponse = await fetch(`${apiUrl}/email/send-invoice-mail`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                body: formData,
            });

            const uploadResult = await readApiResponse(
                uploadResponse,
                "PDF yükleme/e-posta işlemi",
            );

            if (!uploadResult.filePath) {
                throw new Error("PDF yükleme yanıtında filePath bulunamadı.");
            }

            const baseUrl = apiUrl.replace(/\/api$/, "");
            const pdfUrl = `${baseUrl}${uploadResult.filePath.startsWith("/") ? "" : "/"}${
                uploadResult.filePath
            }`;

            const token = localStorage.getItem("token");
            const payload = token ? JSON.parse(atob(token.split(".")[1])) : {};
            const createdBy = payload?.email || payload?.name || "Unknown User";

            const invoiceUrl = isEditMode
                ? `${apiUrl}/invoice/${invoiceId}`
                : `${apiUrl}/invoice`;

            // TCMB rate for the invoice date chosen on the form (not today's rate).
            const exchangeRate = await fetchExchangeRateToTRY(
                selectedShipment.sellingRateCurrency,
                invoiceDate,
            );

            const selectedExemptionOption = [
                ...(exemptionCodes.fullExemption || []),
                ...(exemptionCodes.partialExemption || []),
            ].find((option) => option.code === selectedExemptionCode);
            const exemptionCode = isExemptInvoice ? selectedExemptionOption?.code || "311" : "";
            const exemptionReason = isExemptInvoice
                ? selectedExemptionOption
                    ? `${selectedExemptionOption.code} - ${selectedExemptionOption.label}`
                    : "311 - 14/1 Uluslararası Taşımacılık"
                : "";
            // Per the manager's confirmed rule: only digital freight /
            // international transport / technology invoices are "İstisna"
            // (0% KDV) -- everything else is a regular "Satış" invoice at
            // the standard rate. vatRate here drives both the schema's own
            // amount/vatAmount calculation (invoice.schema.js pre-save
            // hook) and ublBuilder's InvoiceTypeCode selection (ISTISNA vs
            // SATIS is derived from whether any line is at 0%).
            const vatRate = isExemptInvoice ? 0 : STANDARD_VAT_RATE;

            // Due date runs from the invoice date entered on the form.
            const issueDateForDue = fromDateInputValue(invoiceDate);
            const termDays = getPaymentTermDays(customerDetails, getInvoiceSourceType(selectedShipment));
            // No agreed term ("As agreed"): no due date is stored, as before.
            const invoiceDueDate =
                issueDateForDue && termDays
                    ? new Date(issueDateForDue.getTime() + termDays * 86400000)
                    : null;

            const invoiceResponse = await fetch(invoiceUrl, {
                method: isEditMode ? "PUT" : "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                body: JSON.stringify({
                    financeJobId: financeJobId || undefined,
                    shipmentId: selectedShipment._id,
                    shipmentNo: getShipmentDisplayNo(selectedShipment),
                    dblNumber: getShipmentDblNumber(selectedShipment),
                    sourceType: getInvoiceSourceType(selectedShipment),
                    customerName: selectedShipment.customer,
                    email: recipientEmail,
                    exchangeRate,
                    exemptionCode,
                    exemptionReason,
                    invoiceDate: fromDateInputValue(invoiceDate) || undefined,
                    dueDate: invoiceDueDate || undefined,
                    // The stored lines stay as they are; EDM gets the two split
                    // lines built from this at send time.
                    profitSplit: profitSplit.active
                        ? (() => {
                            const technologyOption = [
                                ...(exemptionCodes.fullExemption || []),
                                ...(exemptionCodes.partialExemption || []),
                            ].find((option) => option.code === technologyExemptionCode);

                            return {
                                enabled: true,
                                technologyPercent: profitSplit.percent,
                                normalAmount: profitSplit.normalAmount,
                                technologyAmount: profitSplit.technologyAmount,
                                technologyExemptionCode,
                                technologyExemptionReason: technologyOption
                                    ? `${technologyOption.code} - ${technologyOption.label}`
                                    : technologyExemptionCode,
                            };
                        })()
                        : { enabled: false },
                    totalAmount: totalInvoiceAmountWithVat.toFixed(2),
                    totalCost: totalInvoiceCost,
                    actualCostTotal: totalInvoiceCost,
                    totalCostIncludesBuyingRate: true,
                    costCurrency: selectedShipment.costCurrency || "",
                    totalCostCurrency: selectedShipment.costCurrency || "",
                    sellingRateCurrency: selectedShipment.sellingRateCurrency || "",
                    currency: selectedShipment.sellingRateCurrency || "",
                    pdfUrl,
                    createdBy,
                    invoiceItems: selectedShipment.cargoDetails.map((d) => ({
                        commodity:
                            typeof d.commodity === "object"
                                ? d.commodity.label
                                : d.commodity || "N/A",
                        description: d.description || "",
                        totalCost: safeNumber(d.totalCost),
                        costCurrency: d.costCurrency || selectedShipment.costCurrency || "",
                        unitPrice: d.unitPrice || 0,
                        sellingRateCurrency:
                            d.sellingRateCurrency ||
                            selectedShipment.sellingRateCurrency ||
                            "",
                        unitQuantity: d.unitQuantity || 0,
                        amount: safeNumber(d.unitPrice) * safeNumber(d.unitQuantity),
                        vatRate,
                    })),
                }),
            });

            await readApiResponse(invoiceResponse, "Invoice DB kaydı");

            // The invoice is saved even when the e-mail to the customer failed (e.g. the mail
            // service rejected the request); the user is told the e-mail did not go out.
            if (uploadResult.emailSent === false) {
                appAlert(
                    `⚠️ ${isEditMode ? "Fatura güncellendi" : "Fatura kaydedildi"} ama müşteriye e-posta gönderilemedi` +
                    `${uploadResult.emailError ? ` (${uploadResult.emailError})` : ""}. ` +
                    "Faturayı müşteriye ayrıca iletin.",
                );
            } else {
                showSnackbar(
                    isEditMode
                        ? "✅ Invoice updated successfully."
                        : "📩 Invoice created successfully.",
                    "success",
                );
            }
            handleClose();

            if (!embedded && financeJobId) {
                navigate(`/invoice/finance/jobs/${financeJobId}`, {
                    replace: true,
                    state: { financeJobId, refresh: true },
                });
            }
        } catch (err) {
            console.error("Fatura gönderme hatası:", err);
            appAlert(`⚠️ Fatura gönderilirken hata oluştu: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const tableData = {
        columns: [
            {
                Header: () => (
                    <span style={{ fontWeight: "bold", fontSize: "14px" }}>
            Shipment No
          </span>
                ),
                accessor: "shipmentNo",
                Cell: ({ row }) => (
                    <Tooltip title="Open shipment details" arrow>
            <span
                style={{
                    color: "#2563eb",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontWeight: 700,
                }}
                onClick={() => clickHandler(row.original.rawShipment)}
            >
              {row.original.shipmentNo}
            </span>
                    </Tooltip>
                ),
            },
            {
                Header: () => (
                    <span style={{ fontWeight: "bold", fontSize: "14px" }}>Customer</span>
                ),
                accessor: "customer",
            },
            {
                Header: () => (
                    <span style={{ fontWeight: "bold", fontSize: "14px" }}>
            Total LDM
          </span>
                ),
                accessor: "totalLdm",
            },
            {
                Header: () => (
                    <span style={{ fontWeight: "bold", fontSize: "14px" }}>
            Total CBM
          </span>
                ),
                accessor: "totalCbm",
            },
            {
                Header: () => (
                    <span style={{ fontWeight: "bold", fontSize: "14px" }}>
            Total Volume
          </span>
                ),
                accessor: "totalVolume",
            },
            {
                Header: () => (
                    <span style={{ fontWeight: "bold", fontSize: "14px" }}>
            Selling Rate
          </span>
                ),
                accessor: "sellingRate",
            },
        ],
        rows: shipments.map((shipment, index) => {
            const data = shipment?.attributes || shipment || {};

            return {
                id: data.id || data._id || shipment?.id || shipment?._id,
                shipmentNo: getShipmentLabel(shipment, index),
                customer: data.customer || "N/A",
                // `totals` is a TMS-shipment-only field; a shipment sourced
                // from a finance job has no such object at all, which used
                // to render as a misleading "0" indistinguishable from a
                // shipment that genuinely has zero volume.
                totalLdm: data.totals?.totalLdm ?? "—",
                totalCbm: data.totals?.totalCbm ?? "—",
                totalVolume: data.totals?.totalVolume ?? "—",
                sellingRate: `${getIncomingSellingAmount(data)} ${data.sellingRateCurrency || ""}`,
                rawShipment: shipment,
            };
        }),
    };

    async function createProformaPDF(selectedShipmentArg, customerDetailsArg) {
        return new Promise(async (resolve, reject) => {
            try {
                const doc = new jsPDF();
                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();

                const letterheadImage = await loadImageAsBase64(
                    "/images/letterhead.png",
                );

                const drawLetterhead = () => {
                    doc.addImage(letterheadImage, "PNG", 0, 0, pageWidth, pageHeight);
                };

                const safeText = (v) =>
                    v === undefined || v === null || v === "" ? "N/A" : String(v);

                const fixTR = (t = "") =>
                    String(t)
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, "")
                        .replace(/[ğĞ]/g, "g")
                        .replace(/[şŞ]/g, "s")
                        .replace(/[ıİ]/g, "i")
                        .replace(/[çÇ]/g, "c")
                        .replace(/[öÖ]/g, "o")
                        .replace(/[üÜ]/g, "u");

                const PAGE_TOP = 45;

                drawLetterhead();

                const NAVY = [16, 28, 54];
                const LABEL_FILL = [241, 244, 249];
                const marginX = 14;
                // Letterhead footer band occupies the bottom of every page
                const FOOTER_SPACE = 34;
                const letterheadPages = new Set([1]);

                const mode = getInvoiceSourceType(selectedShipmentArg);
                const MODE_META = {
                    ocean: { title: "OCEAN FREIGHT", refs: "SHIPMENT & B/L REFERENCES", shipperLabel: "Shipper" },
                    air: { title: "AIR FREIGHT", refs: "AIR SHIPMENT & AIR WAYBILL REFERENCES", shipperLabel: "Shipper" },
                    road: { title: "ROAD FREIGHT", refs: "ROAD SHIPMENT & CMR REFERENCES", shipperLabel: "Consignor" },
                };
                const modeMeta = MODE_META[mode] || MODE_META.road;
                // Master document handed over by the consolidation pages (OMBL / AMBL / RMBL)
                const master = pageState.ombl || pageState.ambl || pageState.rmbl || loadedMaster || {};
                const s = selectedShipmentArg || {};
                const c = customerDetailsArg || {};

                const clean = (v) => fixTR(v === undefined || v === null ? "" : String(v).trim());
                const join = (sep, ...parts) => parts.map(clean).filter(Boolean).join(sep);
                const fmtDate = (value) => {
                    if (!value) return "";
                    const date = new Date(value);
                    if (Number.isNaN(date.getTime())) return "";
                    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
                };
                const fmtNumber = (value) =>
                    safeNumber(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                const fmtQty = (value) => safeNumber(value).toLocaleString("en-US", { maximumFractionDigits: 2 });
                const place = (loc) => join(", ", loc?.city, loc?.country);
                const port = (p) => join(", ", p?.port, p?.country);

                // ---- Cargo summary from the shipment's own cargo lines ----
                const cargo = Array.isArray(s.shipmentCargo) ? s.shipmentCargo : [];
                const sumCargo = (key) => cargo.reduce((sum, item) => sum + safeNumber(item?.[key]), 0);
                const grossWeight = sumCargo("grossWeight") || safeNumber(s.grossWeight);
                const cbm = safeNumber(s.totals?.totalCbm) || sumCargo("cbm") || safeNumber(s.cbm);
                const chargeable = safeNumber(s.totals?.totalChargeableWeight) || safeNumber(s.chargeableWeight);
                const ldm = safeNumber(s.totals?.totalLdm) || safeNumber(s.totals?.ldm);
                const pieces = sumCargo("unitQuantity") || safeNumber(s.unitQuantity);
                const packing = clean(
                    cargo.find((item) => item?.selectedPackingType || item?.packingType)?.selectedPackingType ||
                    cargo.find((item) => item?.packingType)?.packingType ||
                    cargo.flatMap((item) => item?.containers || []).find((row) => row?.packingType)?.packingType ||
                    s.selectedPackingType ||
                    s.packingType,
                );
                const commodities = [
                    ...new Set(cargo.map((item) => clean(item?.commodity?.label || item?.commodity)).filter(Boolean)),
                ].join(", ") || clean(s.commodity?.label || s.commodity);
                const piecesText = pieces ? `${fmtQty(pieces)} ${packing || "pcs"}` : "";
                const cargoText = join(
                    " - ",
                    commodities,
                    grossWeight ? `${fmtQty(grossWeight)} kg` : "",
                    mode === "ocean" && cbm ? `${fmtQty(cbm)} CBM` : "",
                    // Ocean: the units entered on the container rows (e.g. "600 IBCS").
                    mode === "road" || mode === "ocean" ? piecesText : "",
                    mode === "road" && ldm ? `${fmtQty(ldm)} LDM` : "",
                );

                // Containers: "2 x 40HC, 1 x 20DC"
                const containerCounts = {};
                cargo
                    .flatMap((item) => (Array.isArray(item?.containers) ? item.containers : []))
                    .forEach((container) => {
                        const type = clean(container?.containerType);
                        if (type) containerCounts[type] = (containerCounts[type] || 0) + 1;
                    });
                const equipment =
                    Object.entries(containerCounts)
                        .map(([type, count]) => `${count} x ${type}`)
                        .join(", ") || clean(s.containerType);
                const containerNos = cargo
                    .flatMap((item) => (Array.isArray(item?.containers) ? item.containers : []))
                    .map((container) => clean(container?.containerNo))
                    .filter(Boolean)
                    .join(", ");

                // ---- Document meta ----
                const shipmentNo = getShipmentLabel(s);
                const invoiceRef = shipmentNo;
                const issueDate = fromDateInputValue(invoiceDate) || new Date();
                const paymentTermDays = getPaymentTermDays(c, mode);
                const dueDate = new Date(issueDate.getTime() + paymentTermDays * 86400000);
                const serviceDate = s.actualDeparture || s.estimatedDeparture || s.pickupDate || master.etd || master.pickupDate;
                const currency = safeText(s.sellingRateCurrency);

                const transportType = clean(s.oceanTransportType || s.airTransportType || s.roadTransportType);
                const service = join(
                    " - ",
                    `International ${mode} freight`,
                    s.operationType,
                    transportType,
                    mode === "road" ? s.truckType : "",
                );
                const masterRefs = Array.isArray(master.carrierReferences) ? master.carrierReferences : [];
                const carrierRef = masterRefs.find((ref) => ref?.mblNumber || ref?.bookingNumber) || {};
                // Typed on the OMBL for carriers without a tracking integration.
                const manualRefs = (Array.isArray(master.manualCarrierReferences) ? master.manualCarrierReferences : [])
                    .filter((ref) => !ref?.shipmentNo || String(ref.shipmentNo) === String(s.shipmentNo || ""));
                const manualValues = (key) => [...new Set(manualRefs.map((ref) => clean(ref?.[key])).filter(Boolean))].join(", ");

                // ---- Title ----
                let y = 34;
                doc.setTextColor(...NAVY);
                doc.setFont("Helvetica", "bold");
                doc.setFontSize(18);
                doc.text("INVOICE", marginX, y);
                doc.setFontSize(9);
                doc.setFont("Helvetica", "normal");
                doc.setTextColor(90, 100, 120);
                doc.text(modeMeta.title, marginX, y + 6);
                doc.setFont("Helvetica", "bold");
                doc.setTextColor(...NAVY);
                doc.setFontSize(10);
                doc.text(invoiceRef, pageWidth - marginX, y, { align: "right" });
                doc.setFont("Helvetica", "normal");
                doc.setFontSize(9);
                doc.text(`Issue date: ${fmtDate(issueDate)}`, pageWidth - marginX, y + 6, { align: "right" });
                doc.setTextColor(0, 0, 0);

                const tableBase = {
                    margin: { top: PAGE_TOP, bottom: FOOTER_SPACE, left: marginX, right: marginX },
                    theme: "grid",
                    styles: { fontSize: 8.5, lineWidth: 0.2, lineColor: [210, 216, 226], cellPadding: 2.2, valign: "middle" },
                    headStyles: { fillColor: NAVY, textColor: 255, fontStyle: "bold", fontSize: 8.5 },
                    willDrawPage: () => {
                        // Any page a table spills onto gets the letterhead exactly once
                        const page = doc.getCurrentPageInfo().pageNumber;
                        if (!letterheadPages.has(page)) {
                            letterheadPages.add(page);
                            drawLetterhead();
                        }
                    },
                };

                // Label/value pairs laid out as 4 columns; empty values are skipped
                const pairTable = (title, pairs, startY) => {
                    const filled = pairs.filter(([, value]) => clean(value));
                    if (!filled.length) return startY;
                    const body = [];
                    for (let i = 0; i < filled.length; i += 2) {
                        const [l1, v1] = filled[i];
                        const [l2, v2] = filled[i + 1] || ["", ""];
                        body.push([l1, clean(v1), l2, clean(v2)]);
                    }
                    autoTable(doc, {
                        ...tableBase,
                        startY,
                        head: [[{ content: title, colSpan: 4 }]],
                        body,
                        columnStyles: {
                            0: { fontStyle: "bold", fillColor: LABEL_FILL, cellWidth: 34 },
                            1: { cellWidth: (pageWidth - marginX * 2 - 68) / 2 },
                            2: { fontStyle: "bold", fillColor: LABEL_FILL, cellWidth: 34 },
                        },
                    });
                    return doc.lastAutoTable.finalY + 5;
                };

                // ---- Parties ----
                const fromText = [
                    "Filenders Lojistik ve Teknoloji A.S",
                    "Mersin University Ciftlikkoy Campus Technopark, 1st Incubation Building 35B/B02, Yenisehir/Mersin, Turkiye",
                    "Tax No: 3871329803",
                    "accounting@filenders.com  |  +90 324 503 84 33",
                ].join("\n");
                const billToText = [
                    clean(s.billTo) || clean(s.customer) || clean(c.name),
                    clean(c.billingAddress) || join(", ", c.city, c.country),
                    join("  |  ", c.taxID ? `Tax ID: ${c.taxID}` : "", c.taxOffice ? `Tax Office: ${c.taxOffice}` : ""),
                    join("  |  ", c.email, c.phone),
                ]
                    .filter(Boolean)
                    .join("\n");

                autoTable(doc, {
                    ...tableBase,
                    startY: y + 12,
                    head: [["INVOICE FROM / SERVICE PROVIDER", "BILL TO / CUSTOMER"]],
                    body: [[fromText, billToText]],
                    styles: { ...tableBase.styles, valign: "top", cellPadding: 3 },
                    columnStyles: { 0: { cellWidth: (pageWidth - marginX * 2) / 2 } },
                });
                y = doc.lastAutoTable.finalY + 5;

                // ---- Document details ----
                y = pairTable(
                    "INVOICE DETAILS",
                    [
                        ["Invoice ref.", invoiceRef],
                        ["Issue date", fmtDate(issueDate)],
                        ["Service date", fmtDate(serviceDate)],
                        ["Due date", paymentTermDays ? fmtDate(dueDate) : "As agreed"],
                        ["Currency", currency],
                        ["Payment terms", "As agreed"],
                        ["Quote no", s.quoteNo],
                        ["Master ref.", s.masterNo || master.omblNumber || master.amblNumber || master.rmblNumber],
                    ],
                    y,
                );

                // ---- Shipment references (per transport mode) ----
                const commonStart = [
                    ["Shipment / File", shipmentNo],
                    ["Service", service],
                ];
                const parties = [
                    [modeMeta.shipperLabel, s.shipper || master.finalMblShipper || master.shipper],
                    ["Consignee", s.consignee || master.finalMblConsignee || master.consignee],
                ];
                const incoterm = [["Incoterm", s.incoterm ? `${clean(s.incoterm)} (Incoterms 2020)` : ""]];

                const refsByMode = {
                    ocean: [
                        ...commonStart,
                        ["Booking", carrierRef.bookingNumber || s.bookingNumber || manualValues("bookingNumber")],
                        ["B/L reference", join(" / ", s.documentTopRightNumber ? `HBL: ${s.documentTopRightNumber}` : "", carrierRef.mblNumber || master.documentTopRightNumber ? `MBL: ${carrierRef.mblNumber || master.documentTopRightNumber}` : "")],
                        ...parties,
                        ["Port of loading", port(s.vesselPol) || place(s.pickupLocation)],
                        ["Port of discharge", port(s.vesselPod) || place(s.deliveryLocation)],
                        ["Vessel / Voyage", join(" / ", s.vesselName || master.vesselName, s.voyageNumber)],
                        ["Carrier", s.shippingLine || carrierRef.carrier || master.lineDetails?.line?.name],
                        ["ETD / ETA", join(" / ", fmtDate(s.actualDeparture || s.estimatedDeparture || master.etd), fmtDate(master.eta || s.estimatedDeliveryDate))],
                        ...incoterm,
                        ["Equipment", equipment],
                        ["Container no", containerNos || s.containerNo || manualValues("containerNumber")],
                        ["Cargo", cargoText],
                    ],
                    air: [
                        ...commonStart,
                        ["MAWB / Airline", join(" / ", master.documentTopRightNumber, master.lineDetails?.line?.name || s.shippingLine)],
                        ["HAWB reference", s.documentTopRightNumber],
                        ...parties,
                        ["Origin / Airport", port(s.vesselPol) || place(s.pickupLocation)],
                        ["Destination / Airport", port(s.vesselPod) || place(s.deliveryLocation)],
                        ["Flight / ETD", join(" / ", s.voyageNumber || s.vesselName || master.vesselName, fmtDate(s.actualDeparture || s.estimatedDeparture || master.etd))],
                        ["ETA / Delivery", join(" / ", fmtDate(master.eta || s.estimatedDeliveryDate), fmtDate(s.actualDelivery))],
                        ...incoterm,
                        ["Cargo", cargoText],
                        ["Gross / Chargeable", join(" / ", grossWeight ? `${fmtQty(grossWeight)} kg gross` : "", chargeable ? `${fmtQty(chargeable)} kg chargeable` : "")],
                        ["Pieces / Volume", join(" / ", piecesText, cbm ? `${fmtQty(cbm)} CBM` : "")],
                    ],
                    road: [
                        ...commonStart,
                        ["CMR reference", s.cmrNumber || master.cmrNumber],
                        ["Truck / Trailer", s.plateContainerNumber || master.plateContainerNumber],
                        ...parties,
                        ["Loading place", place(s.pickupLocation)],
                        ["Delivery place", place(s.deliveryLocation)],
                        ["Pickup / Delivery", join(" / ", fmtDate(s.pickupDate || master.pickupDate), fmtDate(s.actualDelivery || s.estimatedDeliveryDate || master.eta))],
                        ...incoterm,
                        ["Cargo", cargoText],
                    ],
                };
                y = pairTable(modeMeta.refs, refsByMode[mode] || refsByMode.road, y);

                // ---- Charges ----
                const draftLines = s.cargoDetails || [];
                const draftAllInTotal = draftLines.reduce(
                    (sum, d) => sum + safeNumber(d?.unitPrice) * safeNumber(d?.unitQuantity),
                    0,
                );

                // The draft the customer receives shows ONE "All In" line when the
                // invoice has several items. Only this PDF is collapsed: the saved
                // invoice keeps every line, and that is what is sent to EDM.
                const draftItemNames = [
                    ...new Set(
                        draftLines
                            .map((d) => String(d?.commodity?.label || d?.commodity || "").trim())
                            .filter(Boolean),
                    ),
                ].join(", ");

                const invoiceRows =
                    draftLines.length > 1 || profitSplit.active
                        ? [
                            [
                                // The single draft row always names the invoice items it covers.
                                clean(`All-in charges - ${shipmentNo}${draftItemNames ? `\n${draftItemNames}` : ""}`),
                                "1",
                                "Shipment",
                                fmtNumber(draftAllInTotal),
                                fmtNumber(draftAllInTotal),
                            ],
                        ]
                        : draftLines.map((d) => {
                            const qty = safeNumber(d?.unitQuantity);
                            const price = safeNumber(d?.unitPrice);
                            return [
                                join("\n", d?.commodity?.label || d?.commodity, d?.description),
                                fmtQty(qty),
                                clean(d?.unit) || "Shipment",
                                fmtNumber(price),
                                fmtNumber(qty * price),
                            ];
                        });

                autoTable(doc, {
                    ...tableBase,
                    startY: y,
                    head: [["SERVICE DESCRIPTION", "QTY", "UNIT", `UNIT RATE (${currency})`, `AMOUNT (${currency})`]],
                    body: invoiceRows,
                    columnStyles: {
                        1: { halign: "center", cellWidth: 16 },
                        2: { halign: "center", cellWidth: 22 },
                        3: { halign: "right", cellWidth: 32 },
                        4: { halign: "right", cellWidth: 34 },
                    },
                });

                // Draft must show the same figure the actual invoice will be
                // created with -- a VAT-exclusive-only total here would
                // silently understate a "Satış" (non-exempt) invoice by 20%
                // compared to what gets saved/sent to EDM.
                // No exemption clause on the draft: the exemption code/reason
                // only goes to EDM, not onto the customer's draft PDF.
                const vatAmount = isExemptInvoice ? 0 : draftAllInTotal * (STANDARD_VAT_RATE / 100);
                const totalsBody = [
                    ["SUBTOTAL", `${currency} ${fmtNumber(draftAllInTotal)}`],
                    [isExemptInvoice ? "VAT" : `VAT (${STANDARD_VAT_RATE}%)`, isExemptInvoice ? `${currency} 0.00` : `${currency} ${fmtNumber(vatAmount)}`],
                    ["TOTAL", `${currency} ${fmtNumber(draftAllInTotal + vatAmount)}`],
                ];
                autoTable(doc, {
                    ...tableBase,
                    startY: doc.lastAutoTable.finalY + 2,
                    body: totalsBody,
                    margin: { ...tableBase.margin, left: pageWidth - marginX - 90 },
                    tableWidth: 90,
                    columnStyles: {
                        0: { fontStyle: "bold", fillColor: LABEL_FILL },
                        1: { halign: "right" },
                    },
                    didParseCell: (data) => {
                        if (data.row.index === totalsBody.length - 1) {
                            data.cell.styles.fillColor = NAVY;
                            data.cell.styles.textColor = 255;
                            data.cell.styles.fontStyle = "bold";
                        }
                    },
                });

                // ---- Payment details & notes ----
                const BANKS = bankRowsForInvoice([
                    ["T.GARANTI BANKASI", "USD", "TR20 0006 2001 1730 0009 0929 99", "MERSIN SANAYI", "TGBATRISXXX"],
                    ["T.GARANTI BANKASI", "EURO", "TR90 0006 2001 1730 0009 0930 00", "MERSIN SANAYI", "TGBATRISXXX"],
                    ["T.GARANTI BANKASI", "GBP", "TR20 0006 2001 1730 0009 0915 44", "MERSIN SANAYI", "TGBATRISXXX"],
                    ["T.GARANTI BANKASI", "TRY", "TR94 0006 2001 1730 0006 2962 24", "MERSIN SANAYI", ""],
                    ["T.IS BANKASI MERSIN", "USD", "TR34 0006 4000 0026 6030 1533 33", "KUCUK SANAYI", "ISBKTRIS"],
                    ["T.IS BANKASI MERSIN", "EURO", "TR72 0006 4000 0026 6030 1533 28", "KUCUK SANAYI", "ISBKTRIS"],
                    ["T.IS BANKASI MERSIN", "TRY", "TR98 0006 4000 0016 6030 3901 27", "KUCUK SANAYI", ""],
                    ["ZIRAAT BANKASI MERSIN", "USD", "TR80 0001 0019 6097 8746 9350 02", "INONU BULVARI", "TCZBTR2A"],
                    ["ZIRAAT BANKASI MERSIN", "EURO", "TR53 0001 0019 6097 8746 9350 03", "INONU BULVARI", "TCZBTR2A"],
                    ["ZIRAAT BANKASI MERSIN", "TRY", "TR10 0001 0019 6097 8746 9350 01", "INONU BULVARI", ""],
                    ["VAKIFBANK", "USD", "TR72 0001 5001 5804 8024 3766 06", "POZCU MERSIN", ""],
                    ["VAKIFBANK", "EURO", "TR62 0001 5001 5804 8024 3766 89", "POZCU MERSIN", ""],
                    ["VAKIFBANK", "TRY", "TR05 0001 5001 5800 7333 9760 46", "POZCU MERSIN", ""],
                ], { currency, foreign: isForeignCountry(c.customerCountry) });
                // Bank details always start on their own page (page 2) and list
                // every account in full; the table is never split across pages.
                doc.addPage();
                drawLetterhead();
                letterheadPages.add(doc.getCurrentPageInfo().pageNumber);

                autoTable(doc, {
                    ...tableBase,
                    startY: PAGE_TOP,
                    head: [
                        [{ content: `BANK DETAILS  |  Remittance reference: ${invoiceRef}`, colSpan: 5 }],
                        ["BANK", "CURRENCY", "IBAN", "BRANCH", "SWIFT"],
                    ],
                    body: BANKS,
                    pageBreak: "avoid",
                    rowPageBreak: "avoid",
                    styles: { ...tableBase.styles, fontSize: 8 },
                });

                const notesByMode = {
                    ocean: [
                        "Any duties, taxes and excluded fees are billed as agreed.",
                        "Vessel schedules, ETD / ETA and free time are subject to carrier confirmation.",
                        "Bank charges: sender pays (OUR) unless agreed otherwise.",
                    ],
                    air: [
                        "Customs, duties, taxes and destination charges excluded unless agreed.",
                        "Airline schedules, space and screening are subject to change.",
                        "Chargeable weight is the higher of gross and volumetric weight.",
                    ],
                    road: [
                        "Customs, duties and waiting fees excluded unless agreed.",
                        "Subject to signed transport terms and proof of delivery (CMR).",
                        "Toll and fuel surcharges are as stated above.",
                    ],
                };
                const notes = [
                    ...(notesByMode[mode] || notesByMode.road),
                    `Payment is due within the payment term above. Late payments are subject to interest at the prevailing CBRT rates.`,
                    "Please send the SWIFT / transfer confirmation to control.finance@filenders.com and accounting@filenders.com.",
                ];

                autoTable(doc, {
                    ...tableBase,
                    startY: doc.lastAutoTable.finalY + 6,
                    head: [["INVOICE NOTES"]],
                    pageBreak: "avoid",
                    body: [[notes.map((note) => `- ${note}`).join("\n")]],
                    styles: { ...tableBase.styles, fontSize: 8, cellPadding: 3 },
                });

                resolve(doc);
            } catch (err) {
                console.error("PDF oluşturma hatası:", err);
                reject(err);
            }
        });
    }

    const content = (
        <MDBox
            pt={embedded ? 0 : 6}
            pb={3}
            sx={{
                background:
                    "radial-gradient(circle at top left, rgba(37,99,235,0.08), transparent 30%), radial-gradient(circle at top right, rgba(34,197,94,0.08), transparent 30%)",
            }}
        >
            <Card
                sx={{
                    mb: 3,
                    p: 3,
                    borderRadius: "24px",
                    background: "linear-gradient(135deg, #0f172a, #1d4ed8)",
                    boxShadow: "0 22px 55px rgba(15,23,42,0.22)",
                    color: "#fff",
                    overflow: "hidden",
                }}
            >
                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    flexDirection={{ xs: "column", md: "row" }}
                    gap={2}
                >
                    <MDBox>
                        <Chip
                            label="Invoice Workspace"
                            size="small"
                            sx={{
                                color: "#fff",
                                fontWeight: "bold",
                                backgroundColor: "rgba(255,255,255,0.16)",
                                mb: 1,
                            }}
                        />

                        <MDTypography variant="h3" fontWeight="bold" color="white">
                            Invoice Details
                        </MDTypography>

                        <MDTypography variant="button" color="white" sx={{ opacity: 0.82 }}>
                            {isEditMode
                                ? "Review and update the selected customer invoice."
                                : "Create, preview and confirm shipment invoices in one workspace."}
                        </MDTypography>
                    </MDBox>

                    <MDBox display="flex" gap={1} flexWrap="wrap">
                        <MDButton
                            variant="contained"
                            color="light"
                            startIcon={<ReceiptLongIcon />}
                            onClick={handleOpen}
                            disabled={loadingInvoice || !shipments.length}
                        >
                            {loadingInvoice
                                ? "Loading Invoice..."
                                : isEditMode
                                    ? "Edit Invoice"
                                    : "Create Invoice"}
                        </MDButton>

                        <MDButton
                            variant="outlined"
                            color="white"
                            onClick={() => {
                                if (embedded && onClose) return onClose();
                                navigate(-1);
                            }}
                            sx={{
                                color: "#fff",
                                borderColor: "rgba(255,255,255,0.45)",
                            }}
                        >
                            Back
                        </MDButton>
                    </MDBox>
                </MDBox>

                {invoiceLoadError && (
                    <MDBox
                        mt={2}
                        p={1.5}
                        borderRadius="12px"
                        sx={{
                            color: "#B42318",
                            background: "#FEF3F2",
                            border: "1px solid #FECDCA",
                        }}
                    >
                        <MDTypography variant="button" fontWeight="bold">
                            {invoiceLoadError}
                        </MDTypography>
                    </MDBox>
                )}
            </Card>

            <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard title="Shipment Count" value={shipments.length} />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Source Type"
                        value={sourceType || "N/A"}
                        color="dark"
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Total Selling Rate"
                        value={`${totalSellingAmount.toFixed(2)} ${defaultCurrency}`}
                        color="success"
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Draft Status"
                        value={draftGenerated ? "Approved" : "Waiting"}
                        color={draftGenerated ? "success" : "warning"}
                    />
                </Grid>
            </Grid>

            <SectionCard
                title="Linked Shipments"
                subtitle="Select a shipment to generate and confirm invoice details."
            >
                <DataTable table={tableData} canSearch />
            </SectionCard>

            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="xl"
                PaperProps={{
                    sx: {
                        borderRadius: "24px",
                        minHeight: "82vh",
                        background: "linear-gradient(180deg, #ffffff, #f8fafc)",
                        boxShadow: "0 28px 80px rgba(15,23,42,0.24)",
                    },
                }}
            >
                <DialogTitle sx={{ p: 0 }}>
                    <MDBox
                        sx={{
                            px: 3,
                            py: 2.5,
                            background: "linear-gradient(135deg, #0f172a, #2563eb)",
                            color: "#fff",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <MDBox>
                            <MDTypography variant="h5" fontWeight="bold" color="white">
                                {showDetails
                                    ? isEditMode
                                        ? "Edit Invoice"
                                        : "Invoice Preview"
                                    : "Select Shipment"}
                            </MDTypography>
                            <MDTypography
                                variant="caption"
                                color="white"
                                sx={{ opacity: 0.8 }}
                            >
                                {showDetails
                                    ? "Review recipient, line items, bank details and create draft."
                                    : "Choose one shipment to continue invoice preparation."}
                            </MDTypography>
                        </MDBox>

                        <IconButton onClick={handleClose} sx={{ color: "#fff" }}>
                            <CloseIcon />
                        </IconButton>
                    </MDBox>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    {!showDetails ? (
                        <RadioGroup
                            value={selectedShipmentNo}
                            onChange={(e) => setSelectedShipmentNo(e.target.value)}
                        >
                            <Grid container spacing={2}>
                                {shipments.map((shipment, index) => {
                                    const data = shipment?.attributes || shipment || {};
                                    const displayNo = getShipmentDisplayNo(shipment, index);
                                    const displayLabel = getShipmentLabel(shipment, index);
                                    const selected = selectedShipmentNo === displayNo;

                                    return (
                                        <Grid item xs={12} md={6} key={index}>
                                            <Card
                                                onClick={() => setSelectedShipmentNo(displayNo)}
                                                sx={{
                                                    p: 2,
                                                    cursor: "pointer",
                                                    borderRadius: "18px",
                                                    border: selected
                                                        ? "2px solid #2563eb"
                                                        : "1px solid #e5e7eb",
                                                    background: selected
                                                        ? "linear-gradient(135deg, rgba(37,99,235,0.10), #fff)"
                                                        : "#fff",
                                                    boxShadow: selected
                                                        ? "0 14px 35px rgba(37,99,235,0.18)"
                                                        : "0 10px 24px rgba(15,23,42,0.06)",
                                                }}
                                            >
                                                <MDBox display="flex" alignItems="center" gap={1}>
                                                    {/* value: without it, clicking the circle itself reported an
                                                        empty value to the RadioGroup and cleared the selection. */}
                                                    <Radio checked={selected} value={displayNo} />
                                                    <MDBox>
                                                        <MDTypography variant="button" fontWeight="bold">
                                                            {displayLabel}
                                                        </MDTypography>
                                                        <MDTypography
                                                            variant="caption"
                                                            color="text"
                                                            display="block"
                                                        >
                                                            Customer: {data.customer || "N/A"}
                                                        </MDTypography>
                                                        <MDTypography
                                                            variant="caption"
                                                            color="text"
                                                            display="block"
                                                        >
                                                            Selling Rate: {getIncomingSellingAmount(data)}{" "}
                                                            {data.sellingRateCurrency || ""}
                                                        </MDTypography>
                                                    </MDBox>
                                                </MDBox>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </RadioGroup>
                    ) : (
                        <>
                            <Grid container spacing={2} mb={3}>
                                <Grid item xs={12} md={4}>
                                    <StatCard
                                        title="Selected Shipment"
                                        value={getShipmentLabel(selectedShipment)}
                                        color="info"
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <StatCard
                                        title="Customer"
                                        value={selectedShipment?.customer || "N/A"}
                                        color="dark"
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <StatCard
                                        title={isExemptInvoice ? "Invoice Total" : `Invoice Total (incl. ${STANDARD_VAT_RATE}% VAT)`}
                                        value={`${totalInvoiceAmountWithVat.toFixed(2)} ${
                                            selectedShipment?.sellingRateCurrency || ""
                                        }`}
                                        color="success"
                                    />
                                </Grid>
                            </Grid>

                            <SectionCard title="Sender Information">
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Card sx={{ p: 2, borderRadius: "16px", height: "100%" }}>
                                            <MDTypography variant="button" fontWeight="bold">
                                                <FaBuilding
                                                    style={{ marginRight: 6, color: "#059669" }}
                                                />
                                                Company Name
                                            </MDTypography>
                                            <MDTypography
                                                variant="caption"
                                                display="block"
                                                color="text"
                                                mt={0.5}
                                            >
                                                Filenders Lojistik ve Teknoloji A.Ş
                                            </MDTypography>

                                            <Divider sx={{ my: 1.5 }} />

                                            <MDTypography variant="button" fontWeight="bold">
                                                <FaEnvelope
                                                    style={{ marginRight: 6, color: "#d97706" }}
                                                />
                                                Finance Contact
                                            </MDTypography>
                                            <MDTypography
                                                variant="caption"
                                                display="block"
                                                color="text"
                                                mt={0.5}
                                            >
                                                accounting@filenders.com
                                            </MDTypography>

                                            <MDTypography
                                                variant="caption"
                                                display="block"
                                                color="text"
                                                mt={0.5}
                                            >
                                                control.finance@filenders.com
                                            </MDTypography>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Card sx={{ p: 2, borderRadius: "16px", height: "100%" }}>
                                            <MDTypography variant="button" fontWeight="bold">
                                                <FaPhone style={{ marginRight: 6, color: "#0284c7" }} />
                                                Contact
                                            </MDTypography>
                                            <MDTypography
                                                variant="caption"
                                                display="block"
                                                color="text"
                                                mt={0.5}
                                            >
                                                +90 324 503 84 33
                                            </MDTypography>

                                            <Divider sx={{ my: 1.5 }} />

                                            <MDTypography variant="button" fontWeight="bold">
                                                <FaMapMarkerAlt
                                                    style={{ marginRight: 6, color: "#dc2626" }}
                                                />
                                                Address
                                            </MDTypography>
                                            <MDTypography
                                                variant="caption"
                                                display="block"
                                                color="text"
                                                mt={0.5}
                                            >
                                                Mersin University Çiftlikköy Campus Technopark, 1st
                                                Incubation Building 35B/B02, Yenişehir/Mersin
                                            </MDTypography>

                                            <MDTypography
                                                variant="caption"
                                                display="block"
                                                color="text"
                                                mt={0.5}
                                            >
                                                <b>TAX NO:</b> 3871329803
                                            </MDTypography>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </SectionCard>

                            <SectionCard title="Recipient Information">
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            label="Customer"
                                            value={selectedShipment?.customer || ""}
                                            fullWidth
                                            size="small"
                                            sx={FIELD_SX}
                                            InputProps={{ readOnly: true }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            label="Shipment No"
                                            value={getShipmentLabel(selectedShipment)}
                                            fullWidth
                                            size="small"
                                            sx={FIELD_SX}
                                            InputProps={{ readOnly: true }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            label="Invoice Date"
                                            type="date"
                                            value={invoiceDate}
                                            onChange={(e) => setInvoiceDate(e.target.value)}
                                            disabled={invoiceDateLocked}
                                            helperText={
                                                invoiceDateLocked
                                                    ? "E-invoice already sent; the date can no longer change."
                                                    : "Issue date on the invoice and e-invoice."
                                            }
                                            fullWidth
                                            size="small"
                                            sx={FIELD_SX}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ max: toDateInputValue(new Date()) }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            label="Email"
                                            value={customerDetails?.email || "N/A"}
                                            fullWidth
                                            size="small"
                                            sx={FIELD_SX}
                                            InputProps={{ readOnly: true }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            label="Phone"
                                            value={customerDetails?.phone || "N/A"}
                                            fullWidth
                                            size="small"
                                            sx={FIELD_SX}
                                            InputProps={{ readOnly: true }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            label="Tax ID"
                                            value={customerDetails?.taxID || "N/A"}
                                            fullWidth
                                            size="small"
                                            sx={FIELD_SX}
                                            InputProps={{ readOnly: true }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            label="Invoice Address"
                                            value={customerDetails?.billingAddress || "N/A"}
                                            fullWidth
                                            multiline
                                            rows={2}
                                            size="small"
                                            sx={FIELD_SX}
                                            InputProps={{ readOnly: true }}
                                        />
                                    </Grid>
                                </Grid>
                            </SectionCard>

                            <SectionCard
                                title="Invoice Line Items"
                                rightAction={
                                    <MDBox display="flex" gap={1}>
                                        <Autocomplete
                                            options={CURRENCY_OPTIONS}
                                            value={selectedShipment?.costCurrency || ""}
                                            onChange={(_e, newValue) => {
                                                const costCurrency = newValue || "";
                                                setSelectedShipment({
                                                    ...selectedShipment,
                                                    costCurrency,
                                                    cargoDetails: (
                                                        selectedShipment?.cargoDetails || []
                                                    ).map((item) => ({
                                                        ...item,
                                                        costCurrency,
                                                    })),
                                                });
                                            }}
                                            sx={{ width: 160 }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Cost Currency"
                                                    size="small"
                                                    sx={FIELD_SX}
                                                />
                                            )}
                                        />

                                        <Autocomplete
                                            options={CURRENCY_OPTIONS}
                                            value={selectedShipment?.sellingRateCurrency || ""}
                                            onChange={(_e, newValue) => {
                                                const sellingRateCurrency = newValue || "";
                                                setSelectedShipment({
                                                    ...selectedShipment,
                                                    sellingRateCurrency,
                                                    cargoDetails: (
                                                        selectedShipment?.cargoDetails || []
                                                    ).map((item) => ({
                                                        ...item,
                                                        sellingRateCurrency,
                                                    })),
                                                });
                                            }}
                                            sx={{ width: 170 }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Selling Currency"
                                                    size="small"
                                                    sx={FIELD_SX}
                                                />
                                            )}
                                        />
                                    </MDBox>
                                }
                            >
                                {(selectedShipment?.cargoDetails || []).map((detail, index) => (
                                    <Card
                                        key={`cargo-${index}`}
                                        sx={{
                                            p: 2,
                                            mb: 1.5,
                                            borderRadius: "16px",
                                            border: "1px solid #e5e7eb",
                                            boxShadow: "0 8px 20px rgba(15,23,42,0.04)",
                                        }}
                                    >
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} md={2.5}>
                                                <Autocomplete
                                                    options={invoiceOptions}
                                                    value={detail.commodity || ""}
                                                    onChange={(_e, newValue) =>
                                                        updateCargoDetail(
                                                            index,
                                                            "commodity",
                                                            newValue || "",
                                                        )
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Invoice Item"
                                                            size="small"
                                                            sx={FIELD_SX}
                                                        />
                                                    )}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={2.5}>
                                                <TextField
                                                    label="Description"
                                                    value={detail.description || ""}
                                                    onChange={(e) =>
                                                        updateCargoDetail(
                                                            index,
                                                            "description",
                                                            e.target.value,
                                                        )
                                                    }
                                                    fullWidth
                                                    size="small"
                                                    sx={FIELD_SX}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={1.5}>
                                                <TextField
                                                    label={`Total Cost (${
                                                        detail.costCurrency ||
                                                        selectedShipment?.costCurrency ||
                                                        "-"
                                                    })`}
                                                    type="number"
                                                    value={detail.totalCost ?? 0}
                                                    onChange={(e) =>
                                                        updateCargoDetail(
                                                            index,
                                                            "totalCost",
                                                            parseFloat(e.target.value) || 0,
                                                        )
                                                    }
                                                    fullWidth
                                                    size="small"
                                                    sx={FIELD_SX}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={1.5}>
                                                <TextField
                                                    label={`Selling Rate (${
                                                        detail.sellingRateCurrency ||
                                                        selectedShipment?.sellingRateCurrency ||
                                                        "-"
                                                    })`}
                                                    type="number"
                                                    value={detail.unitPrice ?? 0}
                                                    onChange={(e) =>
                                                        updateCargoDetail(
                                                            index,
                                                            "unitPrice",
                                                            parseFloat(e.target.value) || 0,
                                                        )
                                                    }
                                                    fullWidth
                                                    size="small"
                                                    sx={FIELD_SX}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={1.25}>
                                                <TextField
                                                    label="Quantity"
                                                    type="number"
                                                    value={detail.unitQuantity ?? 1}
                                                    onChange={(e) =>
                                                        updateCargoDetail(
                                                            index,
                                                            "unitQuantity",
                                                            parseFloat(e.target.value) || 0,
                                                        )
                                                    }
                                                    fullWidth
                                                    size="small"
                                                    sx={FIELD_SX}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={1.75}>
                                                <TextField
                                                    label="Amount"
                                                    value={`${(
                                                        safeNumber(detail.unitPrice) *
                                                        safeNumber(detail.unitQuantity)
                                                    ).toFixed(
                                                        2,
                                                    )} ${selectedShipment?.sellingRateCurrency || ""}`}
                                                    fullWidth
                                                    size="small"
                                                    sx={FIELD_SX}
                                                    InputProps={{ readOnly: true }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={0.5}>
                                                <MDBox display="flex" gap={0.5}>
                                                    <AddRowButton onClick={addCargoDetail} tooltip="Add cargo" />

                                                    {index > 0 && (
                                                        <RemoveRowButton onClick={() => removeCargoDetail(index)} tooltip="Remove cargo" />
                                                    )}
                                                </MDBox>
                                            </Grid>
                                        </Grid>
                                    </Card>
                                ))}
                            </SectionCard>

                            {selectedShipment?.costBreakdown ? (
                                <SectionCard
                                    title="Cost Summary"
                                    subtitle="House (H) file cost plus master (M) file cost. Internal only - not shown on the customer PDF."
                                >
                                    {(() => {
                                        const breakdown = selectedShipment.costBreakdown;
                                        const currencyCode =
                                            breakdown.currency || selectedShipment?.costCurrency || "";
                                        const masterCost = safeNumber(breakdown.masterCost);
                                        const houseCost = Math.max(totalInvoiceCost - masterCost, 0);
                                        const rows = [
                                            {
                                                label: "H file cost",
                                                hint: getShipmentLabel(selectedShipment),
                                                value: houseCost,
                                            },
                                            ...(masterCost > 0
                                                ? [
                                                    {
                                                        label: "M file cost",
                                                        hint: `${breakdown.masterNo || "Master"}${
                                                            breakdown.masterShipmentCount > 1
                                                                ? ` · split across ${breakdown.masterShipmentCount} H files`
                                                                : ""
                                                        }${
                                                            breakdown.masterParts?.length
                                                                ? ` (${breakdown.masterParts
                                                                    .map((item) => item.label)
                                                                    .join(", ")})`
                                                                : ""
                                                        }`,
                                                        value: masterCost,
                                                    },
                                                ]
                                                : []),
                                        ];

                                        return (
                                            <MDBox maxWidth={520}>
                                                {rows.map((row) => (
                                                    <MDBox
                                                        key={row.label}
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        alignItems="baseline"
                                                        gap={2}
                                                        mb={0.75}
                                                    >
                                                        <MDTypography variant="caption" color="text">
                                                            <b>{row.label}</b>
                                                            {row.hint ? ` · ${row.hint}` : ""}
                                                        </MDTypography>
                                                        <MDTypography variant="caption" fontWeight="bold">
                                                            {row.value.toFixed(2)} {currencyCode}
                                                        </MDTypography>
                                                    </MDBox>
                                                ))}
                                                <Divider sx={{ my: 0.75 }} />
                                                <MDBox display="flex" justifyContent="space-between" gap={2}>
                                                    <MDTypography variant="caption" fontWeight="bold">
                                                        Total cost (H + M)
                                                    </MDTypography>
                                                    <MDTypography variant="caption" fontWeight="bold" color="success">
                                                        {totalInvoiceCost.toFixed(2)} {currencyCode}
                                                    </MDTypography>
                                                </MDBox>
                                            </MDBox>
                                        );
                                    })()}
                                </SectionCard>
                            ) : null}

                            <SectionCard title="Invoice Type (VAT)">
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <RadioGroup
                                            row
                                            value={isExemptInvoice ? "exempt" : "satis"}
                                            onChange={(event) =>
                                                setIsExemptInvoice(event.target.value === "exempt")
                                            }
                                        >
                                            <FormControlLabel
                                                value="exempt"
                                                control={<Radio />}
                                                label="Exempt (0% VAT) — digital freight / international transport / technology"
                                            />
                                            <FormControlLabel
                                                value="satis"
                                                control={<Radio />}
                                                label={`Taxable Sale (${STANDARD_VAT_RATE}% VAT)`}
                                            />
                                        </RadioGroup>
                                    </Grid>

                                    {isExemptInvoice ? (
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                options={[
                                                    ...(exemptionCodes.fullExemption || []),
                                                    ...(exemptionCodes.partialExemption || []),
                                                ]}
                                                getOptionLabel={(option) =>
                                                    option ? `${option.code} - ${option.label}` : ""
                                                }
                                                isOptionEqualToValue={(option, value) => option.code === value.code}
                                                value={
                                                    [
                                                        ...(exemptionCodes.fullExemption || []),
                                                        ...(exemptionCodes.partialExemption || []),
                                                    ].find((option) => option.code === selectedExemptionCode) || null
                                                }
                                                onChange={(event, newValue) =>
                                                    setSelectedExemptionCode(newValue?.code || null)
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="VAT Exemption Reason Code (GİB)"
                                                        helperText="Legal exemption reason for this invoice's 0% VAT line. Default: 311 - Uluslararası Taşımacılık (international transport). For technology invoices: 223."
                                                        sx={FIELD_SX}
                                                    />
                                                )}
                                            />

                                            <MDBox
                                                mt={2}
                                                p={2}
                                                sx={{
                                                    background: "#F9FAFB",
                                                    border: "1px solid #E4E7EC",
                                                    borderRadius: "10px",
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={profitSplitEnabled}
                                                            onChange={(event) => setProfitSplitEnabled(event.target.checked)}
                                                        />
                                                    }
                                                    label="Split profit into a Technology line (EDM only)"
                                                />
                                                <MDTypography variant="caption" color="text" display="block" mb={1}>
                                                    The draft stays a single "All In" line. EDM receives two 0% VAT
                                                    lines: the technology share of the profit under its own exemption
                                                    code, and the rest (cost + remaining profit) under the code above.
                                                </MDTypography>

                                                {profitSplitEnabled ? (
                                                    <>
                                                        <Grid container spacing={2} alignItems="center">
                                                            <Grid item xs={12} md={4}>
                                                                <TextField
                                                                    label="Technology share of profit (%)"
                                                                    type="number"
                                                                    value={technologyPercent}
                                                                    onChange={(event) => setTechnologyPercent(event.target.value)}
                                                                    inputProps={{ min: 0, max: 100, step: 1 }}
                                                                    fullWidth
                                                                    size="small"
                                                                    sx={FIELD_SX}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} md={8}>
                                                                <Autocomplete
                                                                    options={[
                                                                        ...(exemptionCodes.fullExemption || []),
                                                                        ...(exemptionCodes.partialExemption || []),
                                                                    ]}
                                                                    getOptionLabel={(option) =>
                                                                        option ? `${option.code} - ${option.label}` : ""
                                                                    }
                                                                    isOptionEqualToValue={(option, value) => option.code === value.code}
                                                                    value={
                                                                        [
                                                                            ...(exemptionCodes.fullExemption || []),
                                                                            ...(exemptionCodes.partialExemption || []),
                                                                        ].find((option) => option.code === technologyExemptionCode) || null
                                                                    }
                                                                    onChange={(event, newValue) =>
                                                                        setTechnologyExemptionCode(newValue?.code || "223")
                                                                    }
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            label="Technology exemption code (GİB)"
                                                                            size="small"
                                                                            sx={FIELD_SX}
                                                                        />
                                                                    )}
                                                                />
                                                            </Grid>
                                                        </Grid>

                                                        <MDBox mt={1.5} maxWidth={420}>
                                                            {[
                                                                ["Selling", profitSplit.selling],
                                                                ["Cost", profitSplit.cost],
                                                                ["Profit", profitSplit.profit],
                                                            ].map(([label, value]) => (
                                                                <MDBox key={label} display="flex" justifyContent="space-between">
                                                                    <MDTypography variant="caption" color="text">{label}</MDTypography>
                                                                    <MDTypography variant="caption" fontWeight="bold">
                                                                        {value.toFixed(2)} {selectedShipment?.sellingRateCurrency || ""}
                                                                    </MDTypography>
                                                                </MDBox>
                                                            ))}
                                                            <Divider sx={{ my: 0.75 }} />
                                                            <MDBox display="flex" justifyContent="space-between">
                                                                <MDTypography variant="caption" color="text">
                                                                    EDM line 1 - normal ({selectedExemptionCode || "311"})
                                                                </MDTypography>
                                                                <MDTypography variant="caption" fontWeight="bold">
                                                                    {profitSplit.normalAmount.toFixed(2)} {selectedShipment?.sellingRateCurrency || ""}
                                                                </MDTypography>
                                                            </MDBox>
                                                            <MDBox display="flex" justifyContent="space-between">
                                                                <MDTypography variant="caption" color="text">
                                                                    EDM line 2 - technology ({technologyExemptionCode})
                                                                </MDTypography>
                                                                <MDTypography variant="caption" fontWeight="bold">
                                                                    {profitSplit.technologyAmount.toFixed(2)} {selectedShipment?.sellingRateCurrency || ""}
                                                                </MDTypography>
                                                            </MDBox>
                                                            {profitSplit.wholeInvoice ? (
                                                                <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                                                                    100%: the whole selling goes under the technology code, there is no normal line.
                                                                </MDTypography>
                                                            ) : null}
                                                            {!profitSplit.wholeInvoice && profitSplit.profit <= 0 ? (
                                                                <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                                                                    There is no profit (selling is not above cost), so nothing is split.
                                                                </MDTypography>
                                                            ) : null}
                                                        </MDBox>
                                                    </>
                                                ) : null}
                                            </MDBox>
                                        </Grid>
                                    ) : (
                                        <Grid item xs={12}>
                                            <MDBox
                                                sx={{
                                                    background: "#F9FAFB",
                                                    border: "1px solid #E4E7EC",
                                                    borderRadius: "10px",
                                                    px: 2,
                                                    py: 1.5,
                                                }}
                                            >
                                                <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                                                    This invoice will be sent to EDM as a regular taxable sale, with{" "}
                                                    {STANDARD_VAT_RATE}% VAT included:
                                                </MDTypography>
                                                <MDBox display="flex" justifyContent="space-between" maxWidth={280}>
                                                    <MDTypography variant="caption" color="text">Subtotal</MDTypography>
                                                    <MDTypography variant="caption" fontWeight="bold">
                                                        {totalInvoiceAmount.toFixed(2)} {selectedShipment?.sellingRateCurrency || ""}
                                                    </MDTypography>
                                                </MDBox>
                                                <MDBox display="flex" justifyContent="space-between" maxWidth={280}>
                                                    <MDTypography variant="caption" color="text">
                                                        VAT ({STANDARD_VAT_RATE}%)
                                                    </MDTypography>
                                                    <MDTypography variant="caption" fontWeight="bold">
                                                        {(totalInvoiceAmount * (STANDARD_VAT_RATE / 100)).toFixed(2)}{" "}
                                                        {selectedShipment?.sellingRateCurrency || ""}
                                                    </MDTypography>
                                                </MDBox>
                                                <Divider sx={{ my: 0.5, maxWidth: 280 }} />
                                                <MDBox display="flex" justifyContent="space-between" maxWidth={280}>
                                                    <MDTypography variant="caption" fontWeight="bold">Total</MDTypography>
                                                    <MDTypography variant="caption" fontWeight="bold" color="success">
                                                        {totalInvoiceAmountWithVat.toFixed(2)} {selectedShipment?.sellingRateCurrency || ""}
                                                    </MDTypography>
                                                </MDBox>
                                            </MDBox>
                                        </Grid>
                                    )}
                                </Grid>
                            </SectionCard>

                            <SectionCard title="Bank Details">
                                {/* Customer abroad: only the Garanti account in the
                                    selling currency (see finance/shared/bankAccounts). */}
                                {isForeignCountry(customerDetails?.customerCountry) ? (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <BankCard
                                                title="T.GARANTİ BANKASI MERSİN SANAYİ ŞB."
                                                color="#16a34a"
                                                swift="TGBATRISXXX"
                                                rows={bankRowsForInvoice([], {
                                                    currency: selectedShipment?.sellingRateCurrency,
                                                    foreign: true,
                                                }).map(([, currency, iban]) => ({
                                                    currency,
                                                    iban,
                                                    icon: { USD: <FaDollarSign />, EUR: <FaEuroSign />, TRY: <FaLiraSign /> }[currency] || null,
                                                }))}
                                            />
                                        </Grid>
                                    </Grid>
                                ) : (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <BankCard
                                            title="T.GARANTİ BANKASI MERSİN SANAYİ ŞB."
                                            color="#16a34a"
                                            swift="TGBATRISXXX"
                                            rows={[
                                                {
                                                    currency: "USD",
                                                    iban: "TR20 0006 2001 1730 0009 0929 99",
                                                    icon: <FaDollarSign />,
                                                },
                                                {
                                                    currency: "EURO",
                                                    iban: "TR90 0006 2001 1730 0009 0930 00",
                                                    icon: <FaEuroSign />,
                                                },
                                                {
                                                    currency: "TRY",
                                                    iban: "TR94 0006 2001 1730 0006 2962 24",
                                                    icon: <FaLiraSign />,
                                                },
                                            ]}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <BankCard
                                            title="T.İŞ BANKASI MERSİN KÜÇÜK SAN. ŞB."
                                            color="#1d4ed8"
                                            swift="ISBKTRIS"
                                            rows={[
                                                {
                                                    currency: "USD",
                                                    iban: "TR34 0006 4000 0026 6030 1533 33",
                                                    icon: <FaDollarSign />,
                                                },
                                                {
                                                    currency: "EURO",
                                                    iban: "TR72 0006 4000 0026 6030 1533 28",
                                                    icon: <FaEuroSign />,
                                                },
                                                {
                                                    currency: "TRY",
                                                    iban: "TR98 0006 4000 0016 6030 3901 27",
                                                    icon: <FaLiraSign />,
                                                },
                                            ]}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <BankCard
                                            title="ZİRAAT BANKASI MERSİN"
                                            color="#dc2626"
                                            swift="TCZBTR2A"
                                            rows={[
                                                {
                                                    currency: "USD",
                                                    iban: "TR80 0001 0019 6097 8746 9350 02",
                                                    icon: <FaDollarSign />,
                                                },
                                                {
                                                    currency: "EURO",
                                                    iban: "TR53 0001 0019 6097 8746 9350 03",
                                                    icon: <FaEuroSign />,
                                                },
                                                {
                                                    currency: "TRY",
                                                    iban: "TR10 0001 0019 6097 8746 9350 01",
                                                    icon: <FaLiraSign />,
                                                },
                                            ]}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <BankCard
                                            title="VAKIFBANK MERSİN"
                                            color="#f97316"
                                            swift="-"
                                            rows={[
                                                {
                                                    currency: "USD",
                                                    iban: "TR72 0001 5001 5804 8024 3766 06",
                                                    icon: <FaDollarSign />,
                                                },
                                                {
                                                    currency: "EURO",
                                                    iban: "TR62 0001 5001 5804 8024 3766 89",
                                                    icon: <FaEuroSign />,
                                                },
                                                {
                                                    currency: "TRY",
                                                    iban: "TR05 0001 5001 5800 7333 9760 46",
                                                    icon: <FaLiraSign />,
                                                },
                                            ]}
                                        />
                                    </Grid>
                                </Grid>
                                )}
                            </SectionCard>
                        </>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        py: 2,
                        borderTop: "1px solid #e5e7eb",
                        backgroundColor: "rgba(248,250,252,0.92)",
                    }}
                >
                    <MDButton onClick={handleClose} color="dark" variant="outlined">
                        {showDetails ? "Close" : "Cancel"}
                    </MDButton>

                    {!showDetails && (
                        <MDButton
                            onClick={handlePreview}
                            color="info"
                            variant="contained"
                            disabled={!selectedShipmentNo}
                        >
                            Preview
                        </MDButton>
                    )}

                    {showDetails && (
                        <MDButton
                            color="info"
                            variant="contained"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={() => {
                                // Opened on the click itself: a window opened only after the PDF
                                // is built is no longer tied to the click and popup blockers
                                // stopped it, so Draft silently did nothing.
                                const confirmationWindow = window.open("", "_blank");
                                if (!confirmationWindow) {
                                    appAlert("The draft opens in a new window. Please allow pop-ups for this site and click Draft again.");
                                    return;
                                }
                                confirmationWindow.document.write("<p style=\"font-family:sans-serif;padding:24px\">Preparing the invoice draft...</p>");

                                createProformaPDF(selectedShipment, customerDetails)
                                    .then((doc) => {
                                        const pdfUrl = doc.output("bloburl");

                                        confirmationWindow.document.open();
                                        confirmationWindow.document.write(`
                      <html>
                        <head>
                          <title>Invoice Draft</title>
                          <style>
                            body { margin: 0; padding: 0; overflow: hidden; }
                            #approveBtn {
                              position: fixed;
                              top: 20px;
                              right: 20px;
                              padding: 12px 20px;
                              background-color: #16a34a;
                              color: white;
                              border: none;
                              border-radius: 8px;
                              cursor: pointer;
                              font-size: 14px;
                              box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                              z-index: 9999;
                            }
                            embed { width: 100%; height: 100vh; }
                          </style>
                        </head>
                        <body>
                          <embed src="${pdfUrl}" type="application/pdf" />
                          <button id="approveBtn">Approve Draft</button>
                        </body>
                      </html>
                    `);

                                        confirmationWindow.document.close();

                                        // The window was already open, so its load event may have
                                        // passed: the button is wired as soon as it is written.
                                        const btn =
                                            confirmationWindow.document.getElementById(
                                                "approveBtn",
                                            );
                                        if (btn) {
                                            btn.onclick = () => {
                                                setDraftGenerated(true);
                                                confirmationWindow.close();
                                            };
                                        }
                                    })
                                    .catch((err) => {
                                        console.error("PDF oluşturma hatası:", err);
                                        confirmationWindow.close();
                                        appAlert(`The invoice draft could not be created: ${err?.message || err}`);
                                    });
                            }}
                        >
                            Draft
                        </MDButton>
                    )}

                    {showDetails && (
                        <MDButton
                            color="success"
                            variant="contained"
                            startIcon={<CheckCircleIcon />}
                            disabled={!draftGenerated || saving}
                            onClick={handleConfirm}
                        >
                            {saving ? "Saving..." : isEditMode ? "Update Invoice" : "Confirm"}
                        </MDButton>
                    )}
                </DialogActions>
            </Dialog>
        </MDBox>
    );

    if (embedded) return content;

    return (
        <DashboardLayout>
            <DashboardNavbar />
            {content}
            <Footer />
        </DashboardLayout>
    );
};

export default ViewShipments;